import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { Button } from "@app/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@app/ui/components/card";
import { Badge } from "@app/ui/components/badge";
import { Alert, AlertDescription, AlertTitle } from "@app/ui/components/alert";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import { LegalLinks } from "@app/ui";
import {
  Gift,
  GraduationCap,
  BookMarked,
  CalendarClock,
  LogOut,
  AlertCircle,
  Sun,
  Moon,
  ArrowLeftRight,
  Users,
  PlayCircle,
  Plus,
  Clock,
  CheckCircle2,
  FileText,
  Home,
  Library,
  Loader2,
  Bell,
  Eye,
  Inbox,
  Mail,
  Megaphone,
  MessageCircle,
  PictureInPicture2,
  Ban,
  Compass,
  Database,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  ChevronRight,
  Globe,
  Pencil,
  BookOpen,
  Gamepad2,
  HelpCircle,
  Settings,
  Settings2,
  Rocket,
  CheckCircle,
  ClipboardCheck,
  BarChart3,
  StickyNote,
  Grid as GridIcon,
  FileDown,
  Image as ImageIcon,
  Filter,
  Music,
  Keyboard,
  Sparkles,
  UserRound,
  UserCircle,
  CreditCard,
  Wallet,
  LifeBuoy,
  Wand2,
  Mic,
  Share2,
  ArrowDownUp,
  Check,
  ChevronDown,
  Bug
} from "lucide-react";
import {
  api,
  ApiError,
  type AiConnectionsResponse,
  type StudentHomeResponse,
  type UpcomingAssessment,
  type OfferingRow,
  type TurmaMin,
  type BankRow,
  type HomeBank,
  type ProvaWithBank
} from "./api";
import { getActiveTheme, toggleTheme } from "@app/ui/lib/theme";
import { ensurePortalContext } from "@app/ui/lib/portal-context";
import {
  bootstrapFeatureFlags,
  featureFlagsState,
  flagEnabled
} from "@app/ui/lib/feature-flags";
import {
  uiPrefs,
  clearAiCredentials,
  setAiAccessMode,
  setAiAssistantEnabled,
  setAiByok,
  setAiModel,
  setAiModelPreset,
  setAiProvider
} from "./uiPrefs";
import {
  hydrateAiPrefs,
  aiCorrectionPrefs,
  setCelebrateDissertativeBlock,
  setCelebrateEssay
} from "./aiPrefs";
import { useProviderCatalog } from "./aiCatalog";
import { setMusicPlayerEnabled } from "./MusicPlayer";
import {
  BankDetailView,
  activeBankId,
  activeBankName,
  openBank,
  closeBankDetail,
  refreshActiveBank
} from "./BankDetailView";
import { PdfExportModal } from "./PdfExportModal";
import { MarqueeOnHover } from "./Marquee";
import { captureOriginFromEvent, useBodyScrollLock } from "./modalOrigin";
import {
  initAnimations,
  previewAnimationVariant,
  ANIMATION_VARIANTS,
  type AnimationVariant
} from "./animations";
import {
  StudyWorkspaceView,
  studyWorkspace,
  bindStudyPopstate,
  bindStudyKeyboard,
  bindStudyUnloadFlush,
  restoreStudyFromUrl,
  exitStudyWorkspace,
  onStudyWorkspaceExit
} from "./StudyWorkspace";
import {
  ProvaWorkspaceView,
  provaWorkspace,
  bindProvaPopstate,
  bindProvaKeyboard,
  bindProvaUnloadFlush,
  bindProvaTick,
  restoreProvaFromUrl,
  exitProvaWorkspace,
  enterProvaWorkspace,
  onProvaWorkspaceExit
} from "./ProvaWorkspace";
import { ProvaStartModal, openProvaStartModal } from "./ProvaStartModal";
import { ConfirmRoot, PromptRoot, openAlert } from "./ConfirmPromptModal";
import {
  ProvaResultViewComp,
  provaResultView,
  bindProvaResultPopstate,
  restoreProvaResultFromUrl,
  exitProvaResultView,
  enterProvaResultView
} from "./ProvaResultView";
import {
  ProvaRevisaoViewComp,
  provaRevisaoView,
  bindProvaRevisaoPopstate,
  restoreProvaRevisaoFromUrl,
  exitProvaRevisaoView
} from "./ProvaRevisaoView";
import {
  ScheduledExamWorkspaceView,
  scheduledExamView,
  bindScheduledExamPopstate,
  restoreScheduledExamFromUrl,
  exitScheduledExamView
} from "./ScheduledExamWorkspace";
import { MinhasProvasView } from "./MinhasProvasView";
import { JogosView } from "./JogosView";
import { AiStudyView, activeAiStudySession, activeSessionId as activeAiStudySessionId } from "./AiStudyView";
import { RecorderView } from "./RecorderView";
import { SharesView } from "./SharesView";
import { ShareDialog } from "./ShareDialog";
import { RecorderWindow } from "./RecorderWindow";
import { AiStudyHomeCard } from "./AiStudyHomeCard";
import { DonationCard } from "./DonationCard";
import { NotesWindow } from "./NotesWindow";
import { QuickStudyWindow } from "./QuickStudyWindow";
import { QuickStudyDock } from "./QuickStudyDock";
import { GenerateBankDock } from "./GenerateBankDock";
import { TutorIaHero } from "./TutorIaHero";
import { aiStudyTab } from "./AiStudyView";
import { SingleNoteWindow } from "./SingleNoteWindow";
import { AiAssistantWindow } from "./AiAssistantWindow";
import { WelcomeModal, openWelcome, maybeOpenWelcomeFirstTime } from "./WelcomeModal";
// AssinaturaView, AiSpendView e FormaPagamentoView são consumidos pelo
// PagamentosView abaixo (unificou em 1 sidebar item só com tabs internas).
// Não importamos direto aqui porque o ts/lint reclama de "unused".
// [[project-payments-unification]]
import { PagamentosView } from "./PagamentosView";
import { MateriasView } from "./MateriasView";
import { CompraPendenteModal } from "./CompraPendenteModal";
import {
  NotificationsView,
  notifsState,
  startNotificationsPolling
} from "./NotificationsView";
import { NotificationsHomeCard } from "./NotificationsHomeCard";
import { AgendaView, startAgendaPreload, upcomingAulas } from "./AgendaView";
import { ProximosEventosCard } from "./ProximosEventosCard";
import { MinhaContaView } from "./MinhaContaView";
import { MensagensView, inboxUnreadCount, startInboxPolling } from "./MensagensView";
import { InboxCenterView } from "./InboxCenterView";
import { MeusReportsView } from "./MeusReportsView";
import { ConexoesView } from "./ConexoesView";
import { ReferralView } from "./ReferralView";
import { DiscoverBanksView } from "./DiscoverBanksView";
import { AnotacoesHubView } from "./AnotacoesHubView";
import {
  StudyGroupsView,
  GruposEstudoHomeCard,
  loadStudyGroups,
  studyGroupInvites,
  openStudyGroup
} from "./StudyGroups";
import { PeerChatHubView } from "./PeerChatHubView";
import { TurmasView } from "./TurmasView";
import {
  DissertativeWorkspaceView,
  dissertativeWorkspace,
  dissertativeWorkspaceBankName,
  exitDissertativeWorkspace
} from "./DissertativeWorkspace";
import { MoveToFolderMenu } from "./BankFolders";
import { UploadBankModal, openUploadBankModal } from "./UploadBankModal";
import { PeerChatDock } from "./PeerChatDock";
import { InboxDock } from "./InboxDock";
import { MeuAcervoExplorer } from "./MeuAcervoExplorer";
import { FaqView, faqState, loadFaq } from "./FaqView";
import { MensagensHomeCard } from "./MensagensHomeCard";
import { DestaqueHomeCard } from "./DestaqueHomeCard";
import { SimulacoesRecentesCard } from "./SimulacoesRecentesCard";
import { PagamentosHomeCard } from "./PagamentosHomeCard";
import {
  ALL_HOME_BLOCKS,
  appendEmptyRow,
  applyServerHomeLayout,
  BLOCK_SIZE_SPAN,
  enableHomeLayoutServerSync,
  getBlockSize,
  getBlockSpan,
  homeEditMode,
  homeLayout,
  isDesktopViewport,
  isFullWidthBlock,
  moveHomeBlock,
  resetHomeLayout,
  setHomeBlockSize,
  toggleHomeBlock,
  type BlockSize,
  type HomeBlockId
} from "./homeLayout";
import {
  applyOrderToItems as applySidebarOrder,
  applyServerSidebarOrder,
  enableSidebarOrderServerSync,
  loadPlatformSidebarDefault,
  resetSidebarOrder,
  setSidebarOrder,
  sidebarOrder,
  type SidebarKey
} from "./sidebarOrder";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import {
  countCurrentDevicePasskeys,
  registerCurrentDevicePasskey,
  removeCurrentDevicePasskeys,
  supportsBiometria
} from "./BiometriaSection";

// =============================================================================
// Estado
// =============================================================================

type Phase = "loading" | "error" | "ready";
export type NavKey =
  | "home"
  | "aistudy"
  | "quickstudy"
  | "bancos"
  | "disciplinas"
  | "provas"
  | "agenda"
  | "mensagens"
  | "notificacoes"
  | "jogos"
  | "ia"
  | "gravador"
  | "compartilhamentos"
  | "assinatura"
  | "controlegastos"
  | "formapagamento"
  | "meusreports"
  | "conexoes"
  | "descobrir"
  | "anotacoes"
  | "gruposestudo"
  | "peerchat"
  | "turmas"
  | "faq"
  | "indicar"
  | "minhaconta";

const phase = signal<Phase>("loading");
export const data = signal<StudentHomeResponse | null>(null);
const errorMsg = signal<string | null>(null);
const themeSignal = signal<"light" | "dark">(getActiveTheme());
export const activeNav = signal<NavKey>("home");

/** Info de Impersonate exibida pra vítima: quando preenchido, um admin
 *  está vendo o portal como ele em modo READ-ONLY. Renderiza banner
 *  no topo + desabilita mutações. [[project-admin-impersonate-readonly]] */
export interface ImpersonatingInfo {
  actorId: string;
  actorName: string | null;
  startedAt: string;
}
export const impersonatingInfo = signal<ImpersonatingInfo | null>(null);
/** True se a sessão atual é uma impersonação (admin vendo como user). */
export function isImpersonating(): boolean {
  return impersonatingInfo.value !== null;
}

/** Flags globais (admin controla em /manager/) que afetam o que o aluno
 *  vê. Default = tudo visível, preservando comportamento legado quando o
 *  endpoint ainda não respondeu. */
export interface StudentUiVisibility {
  aiMetaPlatform: boolean;
  aiMetaByok: boolean;
  webhooks: boolean;
}
export const studentUiVisibility = signal<StudentUiVisibility>({
  aiMetaPlatform: true,
  aiMetaByok: true,
  webhooks: true
});

/** True quando o usuário corrente possui role `admin` na plataforma. Usado
 *  pra revelar atalhos administrativos (item "Turmas" no menu do aluno) sem
 *  exigir troca de contexto. Detecção via /api/auth/me — profiles inclui
 *  todos os roles que ele tem, independente do contexto ativo. */
export const isPlatformAdmin = signal<boolean>(false);

/** True quando o usuário tem pelo menos UM perfil que NÃO é aluno (professor,
 *  admin_company, platform admin, etc). Usado pra esconder "Trocar perfil"
 *  da sidebar pra quem só é aluno — UX limpa, evita botão que sempre cairia
 *  no alerta "você só tem este perfil". */
export const hasNonStudentProfile = signal<boolean>(false);

// Breadcrumb opcional após a seção (ex: ["ENAMED 2026", "Estudo"])
export interface SubCrumb {
  label: string;
  onClick?: () => void;
}
export const subCrumbs = signal<SubCrumb[]>([]);

type JogosBreadcrumbDetail = {
  label?: string | null;
};

const SECTION_LABEL: Record<NavKey, string> = {
  home: "Início",
  aistudy: "Estudando com IA",
  quickstudy: "Tira-dúvida",
  bancos: "Meu acervo de bancos",
  disciplinas: "Disciplinas",
  provas: "Provas",
  agenda: "Agenda",
  mensagens: "Caixa de entrada",
  notificacoes: "Caixa de entrada",
  jogos: "Jogos",
  ia: "Pagamentos e consumo",
  gravador: "Gravador",
  compartilhamentos: "Compartilhamentos",
  // Os 4 nav keys abaixo caem no mesmo PagamentosView (com tab pré-selecionada),
  // mas mantemos os 4 no enum por retrocompat de deeplinks antigos. O label é
  // o mesmo pra qualquer um exibir o título da página corretamente.
  assinatura: "Pagamentos e consumo",
  controlegastos: "Pagamentos e consumo",
  formapagamento: "Pagamentos e consumo",
  meusreports: "Ajuda / Reportar",
  conexoes: "Conexões",
  descobrir: "Biblioteca de provas",
  anotacoes: "Minhas anotações",
  gruposestudo: "Grupos de Estudo",
  peerchat: "Chat",
  turmas: "Turmas (admin)",
  faq: "Perguntas frequentes",
  indicar: "Indicar amigos",
  minhaconta: "Minha conta"
};

// Considera workspaces ativos pra decidir qual item da sidebar fica destacado.
// Quando o aluno está em Modo Estudo, Prova ou Resultado, todos esses fluxos
// nascem de Bancos — então o botão "Bancos de questões" deve aparecer ativo,
// independente do valor original de `activeNav` (que pode ter ficado em
// outra seção antes de entrar no workspace).
function effectiveNavKey(): NavKey {
  if (studyWorkspace.value) return "bancos";
  if (provaWorkspace.value) return "bancos";
  if (provaResultView.value) return "bancos";
  if (provaRevisaoView.value) return "bancos";
  if (dissertativeWorkspace.value) return "bancos";
  if (scheduledExamView.value) return "provas";
  if (activeAiStudySession.value) return "aistudy";
  // Estudando com IA e Tira-dúvida compartilham a AiStudyView; o destaque do
  // menu segue a aba ativa, mesmo quando o usuário troca de aba dentro da tela.
  if (activeNav.value === "aistudy" || activeNav.value === "quickstudy") {
    return aiStudyTab.value === "quickstudy" ? "quickstudy" : "aistudy";
  }
  return activeNav.value;
}

export function setActiveNav(key: NavKey) {
  const changed = activeNav.value !== key;
  if (changed) subCrumbs.value = [];
  activeNav.value = key;
  if (key === "bancos") {
    // Primeira vez carrega normal; depois disso, refresh silencioso ao
    // voltar pra seção — banco novo criado em outra aba ou pelo professor
    // já aparece sem precisar dar F5.
    if (banksPhase.value === "idle") loadBanks();
    else if (changed) void loadBanks({ silent: true });
  }
  if (key === "home" && changed) {
    // Recarrega a home em silêncio pra refletir bancos novos / progresso
    // de provas / aulas marcadas em outras telas.
    void refreshHome();
  }
  if (key === "gruposestudo") {
    // Carrega grupos + convites pendentes; silencioso ao revisitar.
    void loadStudyGroups({ silent: !changed });
  }
}

async function refreshHome() {
  try {
    const home = await api.home();
    data.value = home;
  } catch (err) {
    console.warn("[home] refresh silencioso falhou:", err);
  }
}

// Re-sincroniza home + bancos quando a aba volta a ficar visível ou ganha
// foco. Cenário comum: aluno tem a tab aberta enquanto o professor cria
// um novo banco em outra tela — sem isso o banco novo só aparece depois
// que o aluno troca de seção. Throttle de 5s pra não martelar a API.
let _lastBgRefresh = 0;
const BG_REFRESH_THROTTLE_MS = 5000;
function maybeBackgroundRefresh() {
  if (typeof document !== "undefined" && document.hidden) return;
  if (phase.value !== "ready") return;
  const now = Date.now();
  if (now - _lastBgRefresh < BG_REFRESH_THROTTLE_MS) return;
  _lastBgRefresh = now;
  // Refresh sempre da home; bancos só se essa seção já foi carregada
  // alguma vez (pra não disparar fetch desnecessário em alunos que nunca
  // entraram em Bancos).
  void refreshHome();
  if (banksPhase.value !== "idle") void loadBanks({ silent: true });
}
if (typeof window !== "undefined") {
  window.addEventListener("focus", maybeBackgroundRefresh);
  document.addEventListener("visibilitychange", maybeBackgroundRefresh);
}

// Bancos
type BanksPhase = "idle" | "loading" | "error" | "ready";
export const banksPhase = signal<BanksPhase>("idle");
export const banksData = signal<BankRow[]>([]);
const banksError = signal<string | null>(null);

export async function loadBanks(opts: { silent?: boolean } = {}) {
  // silent: refresh em background (pós-sair do estudo). Mantém phase=ready
  // e a lista atual visível até o novo payload chegar — sem piscar skeleton.
  if (!opts.silent) banksPhase.value = "loading";
  banksError.value = null;
  try {
    const res = await api.banks();
    banksData.value = res.banks;
    banksPhase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    if (opts.silent) {
      // Falhar em silêncio não troca a UI pra "error" — a lista anterior
      // continua útil. Só loga pro console.
      console.error("[banks] silent reload falhou:", err);
      return;
    }
    banksError.value = err instanceof Error ? err.message : String(err);
    banksPhase.value = "error";
  }
}

// Desktop: icon-only mode (persiste em localStorage). Mobile: overlay aberto/fechado.
const SIDEBAR_COLLAPSED_KEY = "student-new-sidebar-collapsed";
const initialCollapsed = (() => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
})();
const sidebarCollapsed = signal<boolean>(initialCollapsed);
const sidebarMobileOpen = signal<boolean>(false);

// Track largura pra que o estado `collapsed` (icon-only) só se aplique no
// desktop. No mobile (drawer overlay), sempre mostramos ícone + label —
// não faz sentido colapsar porque já existe o backdrop pra fechar.
const isMobileViewport = signal<boolean>(
  typeof window !== "undefined" ? window.innerWidth < 768 : false
);
if (typeof window !== "undefined") {
  const mq = window.matchMedia("(max-width: 767px)");
  isMobileViewport.value = mq.matches;
  // matchMedia event listener (sintaxe nova com fallback antigo)
  const onChange = (e: MediaQueryListEvent) => {
    isMobileViewport.value = e.matches;
  };
  if (mq.addEventListener) mq.addEventListener("change", onChange);
  else mq.addListener(onChange);
}

// Modais (Guia do Usuário, Preferências)
const guiaOpen = signal<boolean>(false);
const prefsOpen = signal<boolean>(false);

// Preferências do aluno. Comemoração e vibração já estão wired aos
// recursos (animations.ts + vibrate); swipe, player e biometria também
// refletem comportamento real no app novo.
interface PrefsState {
  comemoracao: boolean;
  comemoracaoStyles: {
    confete: boolean;
    fogos: boolean;
    estrelas: boolean;
    emojis: boolean;
    baloes: boolean;
    champanhe: boolean;
  };
  vibracao: boolean;
  // Sub-toggles de vibração: por tipo de feedback. Permite ligar só
  // "errou" + desligar "acertou" e vice-versa.
  vibracaoTipos: {
    correct: boolean;
    wrong: boolean;
    tap: boolean;
  };
  swipe: boolean;
  musicPlayer: boolean;
  biometria: boolean;
  /** Mostra um dock flutuante no canto inferior direito (somente desktop)
   *  com prévia das mensagens não-lidas. Opt-in. */
  mensagensDock: boolean;
  /** Abre automaticamente uma janelinha do PeerChatDock quando chega uma
   *  mensagem nova de uma conexão. Opt-in (default false) pra não assustar
   *  quem prefere acessar pela view dedicada.
   *  [[project-peer-chat-autoopen-toggle]] */
  peerChatAutoOpen: boolean;
}

const PREFS_KEY = "student-new-prefs";
const DEFAULT_PREFS: PrefsState = {
  comemoracao: true,
  comemoracaoStyles: {
    confete: true,
    fogos: true,
    estrelas: true,
    emojis: true,
    baloes: true,
    champanhe: true
  },
  vibracao: true,
  vibracaoTipos: {
    correct: true,
    wrong: true,
    tap: false
  },
  swipe: true,
  musicPlayer: true,
  biometria: false,
  mensagensDock: false,
  peerChatAutoOpen: false
};

function loadPrefs(): PrefsState {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      // Merge com DEFAULT pra usuários antigos que ainda não têm os
      // campos novos (vibracaoTipos, comemoracaoStyles completo, etc).
      const saved = JSON.parse(raw) as Partial<PrefsState>;
      return {
        ...DEFAULT_PREFS,
        ...saved,
        comemoracaoStyles: {
          ...DEFAULT_PREFS.comemoracaoStyles,
          ...(saved.comemoracaoStyles ?? {})
        },
        vibracaoTipos: {
          ...DEFAULT_PREFS.vibracaoTipos,
          ...(saved.vibracaoTipos ?? {})
        }
      };
    }
  } catch {
    // ignora
  }
  return DEFAULT_PREFS;
}
export const prefs = signal<PrefsState>(loadPrefs());
setMusicPlayerEnabled(prefs.value.musicPlayer);

// Sync com o backend (debounced). O snapshot mais recente de prefs.value é
// sempre enviado — coalescemos múltiplos toggles seguidos num único PUT.
let _prefsSyncTimer: ReturnType<typeof setTimeout> | null = null;
const PREFS_SYNC_DEBOUNCE_MS = 900;
function schedulePrefsSync() {
  if (_prefsSyncTimer) clearTimeout(_prefsSyncTimer);
  _prefsSyncTimer = setTimeout(() => {
    _prefsSyncTimer = null;
    // Inclui homeLayout no payload pra preservar o snapshot quando o cliente
    // faz PUT substituição total no student-prefs. Sem isso, mudar uma pref
    // qualquer apagaria o homeLayout do server.
    const payload = {
      ...(prefs.value as unknown as Record<string, unknown>),
      homeLayout: homeLayout.value
    };
    void api
      .savePrefs(payload)
      .catch((err) => console.error("[prefs] sync server falhou:", err));
  }, PREFS_SYNC_DEBOUNCE_MS);
}

export function savePrefs(next: PrefsState) {
  prefs.value = next;
  setMusicPlayerEnabled(next.musicPlayer);
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {
    // ignora
  }
  schedulePrefsSync();
}

// Load das prefs do servidor uma vez ao bootstrap. Server > localStorage:
// se houver prefs salvas remotamente, elas substituem o que estava local
// (espelha o aluno entre dispositivos). Se servidor não tem nada, mantém
// o local. Falhas são silenciosas — local funciona standalone.
let _prefsLoadStarted = false;
function loadPrefsFromServerOnce() {
  if (_prefsLoadStarted) return;
  _prefsLoadStarted = true;
  void api
    .getPrefs()
    .then((res) => {
      const remote = res.prefs;
      if (!remote || typeof remote !== "object") return;
      // Merge profundo com DEFAULT pra suportar campos novos que ainda não
      // estão no remoto.
      const next: PrefsState = {
        ...DEFAULT_PREFS,
        ...(remote as Partial<PrefsState>),
        comemoracaoStyles: {
          ...DEFAULT_PREFS.comemoracaoStyles,
          ...((remote as Partial<PrefsState>).comemoracaoStyles ?? {})
        },
        vibracaoTipos: {
          ...DEFAULT_PREFS.vibracaoTipos,
          ...((remote as Partial<PrefsState>).vibracaoTipos ?? {})
        }
      };
      prefs.value = next;
      setMusicPlayerEnabled(next.musicPlayer);
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      } catch { /* ignora */ }
      // homeLayout (cross-device): se o server tem snapshot, aplica.
      // Independente disso, ativa o sync pra futuras mutações irem pro server.
      const hl = (remote as Record<string, unknown>).homeLayout;
      if (hl && typeof hl === "object") applyServerHomeLayout(hl);
      enableHomeLayoutServerSync();
      // sidebarOrder (cross-device): mesma estratégia.
      const so = (remote as Record<string, unknown>).sidebarOrder;
      if (Array.isArray(so)) applyServerSidebarOrder(so);
      enableSidebarOrderServerSync();
      // Fallback: se nem custom local nem custom do usuário, aplica o
      // default da plataforma (admin pode ter definido um pra todos).
      void loadPlatformSidebarDefault();
    })
    .catch((err) => console.error("[prefs] load server falhou:", err));
}

// Conecta o módulo de animações ao signal de prefs — toda mudança nas
// preferências reflete imediatamente nas próximas animações disparadas
// sem precisar re-registrar. Roda uma vez ao carregar o módulo.
initAnimations({
  enabled: () => prefs.value.comemoracao,
  variants: () => prefs.value.comemoracaoStyles,
  vibrationEnabled: () => prefs.value.vibracao,
  vibrationGranular: () => prefs.value.vibracaoTipos
});

export function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  try {
    localStorage.setItem(
      SIDEBAR_COLLAPSED_KEY,
      sidebarCollapsed.value ? "1" : "0"
    );
  } catch {
    // ignora
  }
}

// Toggle responsivo: desktop colapsa/expande largura; mobile abre/fecha drawer.
// Exposto pra main.tsx chamar via mountSidebarShortcuts (Ctrl+/ Ctrl+\ Ctrl+B).
export function toggleSidebarResponsive() {
  if (typeof window === "undefined") return;
  if (window.matchMedia("(min-width: 768px)").matches) {
    toggleSidebar();
  } else {
    sidebarMobileOpen.value = !sidebarMobileOpen.value;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("theme:changed", (e: any) => {
    themeSignal.value = e?.detail?.theme ?? getActiveTheme();
  });

  // Esc fecha modais abertos (Guia ou Preferências)
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    const isEditable =
      !!target &&
      (target.isContentEditable ||
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT");

    if (e.key === "Escape") {
      if (guiaOpen.value) {
        guiaOpen.value = false;
        e.preventDefault();
      } else if (prefsOpen.value) {
        prefsOpen.value = false;
        e.preventDefault();
      }
      return;
    }

    if (isEditable || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === "h" || e.key === "H") {
      guiaOpen.value = !guiaOpen.value;
      e.preventDefault();
      return;
    }
    if (e.key === "t" || e.key === "T") {
      toggleTheme();
      e.preventDefault();
    }
  });

  // Modo Estudo: popstate (back/forward) + restore se a URL ja vem com
  // /student/banks/:bankId/estudo + atalhos de teclado (setas + A-E)
  // + flush de save pendente em beforeunload/pagehide.
  bindStudyPopstate();
  bindStudyKeyboard();
  bindStudyUnloadFlush();
  // Quando o aluno sai do workspace de estudo, refaz a lista de bancos em
  // background. Sem isso, voltar pra "Banco de questões" mostra os contadores
  // antigos (respondidas, acertos %) — confuso após ter acabado de responder.
  // Só refaz se a lista já foi carregada uma vez (banksPhase !== "idle"),
  // pra não disparar fetch desnecessário se o aluno nunca abriu essa seção.
  onStudyWorkspaceExit(() => {
    if (banksPhase.value !== "idle") void loadBanks({ silent: true });
    // Bug 2026-05-19: ao voltar ao detalhe do banco via breadcrumb, o card
    // mostrava contadores velhos. Refaz a query de detalhe em silent.
    void refreshActiveBank();
  });
  restoreStudyFromUrl();

  // Simulação de Prova: mesmas mecânicas (popstate + keyboard + flush no
  // unload + tick 500ms pros cronômetros) + restore de URL pra reentrar
  // depois de refresh.
  bindProvaPopstate();
  bindProvaKeyboard();
  bindProvaUnloadFlush();
  bindProvaTick();
  onProvaWorkspaceExit(() => {
    if (banksPhase.value !== "idle") void loadBanks({ silent: true });
    void refreshActiveBank();
  });
  restoreProvaFromUrl();
  bindProvaResultPopstate();
  restoreProvaResultFromUrl();
  bindProvaRevisaoPopstate();
  restoreProvaRevisaoFromUrl();
  bindScheduledExamPopstate();
  restoreScheduledExamFromUrl();
}

async function bootstrap() {
  phase.value = "loading";
  errorMsg.value = null;
  // Antes de qualquer fetch do portal, garante que o contexto ativo da
  // sessão é "student". Se o usuário entrou aqui depois de ter usado
  // outro portal (manager, teacher, company), o auto-switch resolve sem
  // jogar ele de volta pro picker.
  const ctxOk = await ensurePortalContext(
    { kind: "company", roles: ["student"] },
    "/auth/"
  );
  if (!ctxOk.ok) return; // já redirecionou
  try {
    const home = await api.home();
    data.value = home;
    // Detecta Impersonate cedo: chama /api/auth/me em paralelo pra ler
    // o flag `impersonating`. Sem isso, o aluno não saberia visualmente
    // que está sendo "visto" por um admin. [[project-admin-impersonate-readonly]]
    void api.me().then((me) => {
      const meTyped = me as {
        impersonating?: ImpersonatingInfo;
        studentUiVisibility?: StudentUiVisibility;
        profiles?: Array<{ kind: string; role: string }>;
        aiCorrectionPrefs?: unknown;
        aiAssistantPrefs?: unknown;
        quickStudyPrefs?: unknown;
      };
      impersonatingInfo.value = meTyped.impersonating ?? null;
      if (meTyped.studentUiVisibility) {
        studentUiVisibility.value = meTyped.studentUiVisibility;
      }
      // Hidrata as prefs de IA (corretor + assistente). Defaults aplicados
      // automaticamente quando payload não traz a chave.
      hydrateAiPrefs({
        aiCorrectionPrefs: meTyped.aiCorrectionPrefs,
        aiAssistantPrefs: meTyped.aiAssistantPrefs,
        quickStudyPrefs: meTyped.quickStudyPrefs
      });
      // Detecta role de admin de plataforma pra revelar item "Turmas" no menu.
      // Não depende do contexto ativo — usuário pode estar logado como aluno
      // mas ter role admin disponível.
      isPlatformAdmin.value = !!meTyped.profiles?.some(
        (p) => p.kind === "platform" && p.role === "admin"
      );
      // Sidebar mostra "Trocar perfil" só pra quem tem algo além de aluno.
      // Múltiplas matrículas como aluno (em escolas diferentes) NÃO contam
      // — esses alunos usam o seletor de contexto interno, não trocam perfil.
      hasNonStudentProfile.value = !!meTyped.profiles?.some(
        (p) => p.role !== "student"
      );
    }).catch(() => { /* ignore */ });
    void bootstrapFeatureFlags("student");
    phase.value = "ready";
    // Segurança BYOK: se a chave salva localmente foi por OUTRO user (mesmo
    // navegador com mais de um aluno), descarta. Evita que aluno B veja/use
    // chave de aluno A só por ter feito login no PC da escola.
    const savedFor = uiPrefs.value.aiByokSavedForUserId;
    if (savedFor && savedFor !== home.user.id) {
      clearAiCredentials();
    }
    // Load das prefs do servidor em paralelo — não bloqueia a UI. Se o
    // aluno tem prefs salvas em outro dispositivo, elas substituem o
    // local. Falha silencioso (local segue funcionando).
    loadPrefsFromServerOnce();
    // Inicia polling de notificações (1ª fetch + tick de 60s).
    startNotificationsPolling();
    // Polling de mensagens (idem; badge da sidebar usa isso).
    startInboxPolling();
    // Carrega agenda do aluno em background pra alimentar "Próximos eventos"
    // na home + página de Agenda já com dados prontos.
    startAgendaPreload();
    // Estado do FAQ — decide se item aparece no menu. Falha silenciosa
    // (item simplesmente não aparece).
    void loadFaq();
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errorMsg.value = err instanceof Error ? err.message : String(err);
    phase.value = "error";
  }
}

async function handleLogout() {
  // Limpa credenciais BYOK locais — proteção pra PCs compartilhados.
  // O próximo aluno que logar não herda a chave do anterior.
  clearAiCredentials();
  await api.logout().catch(() => {});
  window.location.href = "/auth/";
}

async function goSwitch() {
  // Antes de navegar pro picker, checa quantos perfis o user tem. Se só 1,
  // dá feedback no lugar de levar pra uma tela vazia.
  try {
    const me = await fetch("/api/auth/me", { credentials: "include" }).then(
      (r) => r.json()
    );
    const profiles: any[] = me?.profiles || [];
    if (profiles.length <= 1) {
      void openAlert({ title: "Aviso", message: "Você só tem este perfil cadastrado — não há outro pra alternar." });
      return;
    }
  } catch {
    // Em caso de erro, segue o fluxo padrão (deixa o picker tratar)
  }
  window.location.href = "/auth/?picker=1";
}

// =============================================================================
// Helpers visuais
// =============================================================================

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function timeUntil(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  const diff = d - Date.now();
  if (diff <= 0) return null;
  const minutes = Math.round(diff / 60000);
  if (minutes < 60) return `em ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `em ${hours}h`;
  const days = Math.round(hours / 24);
  return `em ${days}d`;
}

const kindLabel: Record<UpcomingAssessment["kind"], string> = {
  prova: "Prova",
  trabalho: "Trabalho",
  simulada: "Simulada"
};

// =============================================================================
// Sidebar
// =============================================================================

/** Badge tipada por cor — usada quando o item exibe DUAS (ou mais) contagens
 *  vindas de domínios diferentes (ex: Caixa = notificações + mensagens).
 *  Substitui o `badge` único quando definida. */
interface NavBadge {
  count: number;
  color: "violet" | "amber";
  title: string;
}
interface NavItem {
  key: NavKey;
  label: string;
  icon: typeof Home;
  badge?: string;
  /** Lista de badges (cada uma com sua cor) — tem precedência sobre `badge`
   *  quando presente. Renderizadas lado a lado no canto direito do item. */
  badges?: NavBadge[];
  /** Sub-itens opcionais. Quando presente, item vira "pai colapsável":
   *  click toggla expand em vez de navegar. [[project-sidebar-submenus-backlog]] */
  children?: Array<{ key: NavKey; label: string; icon?: typeof Home; badge?: string }>;
}

/** Pill colorida por domínio — usada em itens com múltiplas contagens (ex:
 *  Caixa de entrada com notificações + mensagens). Mantém o contraste em
 *  cima do gradient do item ativo. */
function NavBadgePill({
  count,
  color,
  title,
  onActive
}: {
  count: number;
  color: "violet" | "amber";
  title: string;
  onActive: boolean;
}) {
  const display = count > 99 ? "99+" : String(count);
  const cls = onActive
    ? "bg-white/95 ring-1 " +
      (color === "amber" ? "text-amber-600 ring-amber-200/60" : "text-primary ring-white/30")
    : color === "amber"
      ? "bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300"
      : "bg-primary/15 text-primary ring-1 ring-primary/25";
  return (
    <span
      title={title}
      aria-label={title}
      class={`inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ${cls}`}
    >
      {display}
    </span>
  );
}

/** Modo de reordenação ativo no sidebar (desktop only). */
export const sidebarEditMode = signal<boolean>(false);

/** Wrapper SortableContext do sidebar — vertical, sem clique pra ativar o item
 *  (já que estamos no modo de edição). Cada item vira "draggable" via handle. */
function SortableNav({
  items,
  onReorder
}: {
  items: NavItem[];
  onReorder: (next: string[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );
  function handleEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.key === active.id);
    const newIdx = items.findIndex((i) => i.key === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = items.slice();
    const [moved] = next.splice(oldIdx, 1);
    next.splice(newIdx, 0, moved);
    onReorder(next.map((i) => i.key));
  }
  return (
    <DndContext sensors={sensors} onDragEnd={handleEnd}>
      <SortableContext
        items={items.map((i) => i.key)}
        strategy={rectSortingStrategy}
      >
        {items.map((item) => (
          <SortableNavItem key={item.key} item={item} />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableNavItem({ item }: { item: NavItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  } as any;
  const Icon = item.icon;
  return (
    <div
      ref={setNodeRef}
      style={style}
      class="flex w-full items-center gap-2 rounded-md border border-dashed border-violet-500/40 bg-violet-500/5 px-2 py-2 text-sm"
    >
      <button
        type="button"
        {...(attributes as any)}
        {...(listeners as any)}
        title="Arraste para reordenar"
        aria-label={`Mover ${item.label}`}
        class="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-accent active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </button>
      <Icon width="1.1em" height="1.1em" className="shrink-0 text-violet-600" strokeWidth={2} />
      <span class="flex-1 truncate text-foreground">{item.label}</span>
    </div>
  );
}

function NavLink({
  item,
  isActive,
  collapsed,
  onClick,
  activeKey,
  onChildClick
}: {
  item: NavItem;
  isActive: boolean;
  collapsed: boolean;
  onClick: () => void;
  /** Quando item tem children, usamos isso pra detectar se algum filho
   *  está ativo (mantém o pai expandido). Sem children, ignorado. */
  activeKey?: NavKey;
  /** Click em filho. Necessário só quando item.children existe. */
  onChildClick?: (childKey: NavKey) => void;
}) {
  const Icon = item.icon;
  const hasChildren = !!item.children && item.children.length > 0;
  const childActive = hasChildren && activeKey
    ? item.children!.some((c) => c.key === activeKey)
    : false;
  // Estado expand local (com persistência via sidebar-tree).
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (!hasChildren) return false;
    try {
      const raw = localStorage.getItem("sidebar.expanded.student");
      const arr = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(arr) && arr.includes(item.key)) return true;
    } catch {
      /* ignore */
    }
    return childActive;
  });
  // Mantém expandido enquanto há filho ativo (UX: link direto pra filho).
  useEffect(() => {
    if (childActive && !expanded) setExpanded(true);
  }, [childActive]);
  function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    try {
      const raw = localStorage.getItem("sidebar.expanded.student");
      const arr = raw ? (JSON.parse(raw) as unknown) : null;
      const set = new Set<string>(Array.isArray(arr) ? (arr as string[]) : []);
      if (next) set.add(item.key);
      else set.delete(item.key);
      localStorage.setItem("sidebar.expanded.student", JSON.stringify(Array.from(set)));
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={hasChildren ? toggleExpanded : onClick}
        title={collapsed ? item.label : undefined}
        {...(hasChildren ? { "aria-expanded": expanded } : {})}
        class={`group relative flex w-full items-center rounded-md text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
          collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
        } ${
          isActive || childActive
            ? "bg-brand-gradient text-white shadow-md shadow-primary/30 ring-1 ring-white/10 dark:text-white"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`}
      >
        <Icon width="1.3em" height="1.3em" className="shrink-0" strokeWidth={2} />
        {!collapsed && (
          <>
            <span class="flex-1 text-left">{item.label}</span>
            {item.badges && item.badges.length > 0 ? (
              <span class="inline-flex items-center gap-1">
                {item.badges.map((b, i) => (
                  <NavBadgePill
                    key={i}
                    count={b.count}
                    color={b.color}
                    title={b.title}
                    onActive={isActive || childActive}
                  />
                ))}
              </span>
            ) : item.badge ? (
              <span
                class={`inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
                  isActive || childActive
                    ? "bg-white/90 text-primary"
                    : "bg-primary/15 text-primary ring-1 ring-primary/25"
                }`}
              >
                {item.badge}
              </span>
            ) : null}
            {hasChildren && (
              <ChevronDown
                width="1em"
                height="1em"
                class={`shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            )}
          </>
        )}
        {collapsed && item.badges && item.badges.length > 0 && (
          // Sidebar colapsada: empilha pontos coloridos no canto pra preservar
          // a distinção entre mensagens (âmbar) e notificações (violeta).
          <span class="absolute right-1 top-1 flex flex-col items-end gap-0.5">
            {item.badges.map((b, i) => (
              <span
                key={i}
                title={b.title}
                class={`grid size-3.5 place-items-center rounded-full text-[8px] font-bold text-white ring-1 ring-card ${
                  b.color === "amber" ? "bg-amber-500" : "bg-primary"
                }`}
              >
                {b.count > 9 ? "9+" : b.count}
              </span>
            ))}
          </span>
        )}
        {collapsed && !item.badges && item.badge && (
          <span class="absolute right-1 top-1 grid size-4 place-items-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground ring-1 ring-card">
            {item.badge}
          </span>
        )}
      </button>
      {/* Filhos colapsáveis — só renderiza no desktop expandido. No mobile
          drawer o mesmo padrão pode ser usado depois; por enquanto fica só
          desktop pra evitar regressão no mobile. */}
      {hasChildren && expanded && !collapsed && (
        <div class="ml-3 mt-1 flex flex-col gap-0.5 border-l border-border pl-3">
          {item.children!.map((child) => {
            const ChildIcon = child.icon;
            const childIsActive = activeKey === child.key;
            return (
              <button
                key={child.key}
                type="button"
                onClick={() => onChildClick?.(child.key)}
                class={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  childIsActive
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {ChildIcon && (
                  <ChildIcon width="1em" height="1em" className="shrink-0" />
                )}
                <span class="flex-1 text-left">{child.label}</span>
                {child.badge && (
                  <span class="rounded-full bg-primary/15 px-1.5 text-[10px] font-bold text-primary">
                    {child.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

// Botão "abrir menu" do mobile: hamburguer + dotzinho quando há mensagens ou
// notificações não-lidas (sinaliza pro usuário que tem coisa ali dentro).
function MobileMenuButton() {
  const unreadNotifs = notifsState.value.unreadCount;
  const unreadMessages = inboxUnreadCount.value;
  const hasUnread = unreadNotifs > 0 || unreadMessages > 0;
  return (
    <button
      type="button"
      onClick={() => (sidebarMobileOpen.value = true)}
      aria-label={
        hasUnread
          ? `Abrir menu — ${unreadNotifs + unreadMessages} item(ns) novo(s)`
          : "Abrir menu"
      }
      class="relative grid size-9 shrink-0 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
    >
      <Menu className="size-4" />
      {hasUnread && (
        <span
          class="absolute right-1 top-1 inline-block size-2 rounded-full bg-primary ring-2 ring-background"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

function Sidebar({ home }: { home: StudentHomeResponse }) {
  const upcomingCount = home.upcoming.length;
  const unreadNotifs = notifsState.value.unreadCount;
  const unreadMessages = inboxUnreadCount.value;
  // Subscribe ao state das flags pra re-renderizar se o admin mudar config no
  // mesmo session (re-bootstrap acionado em algum lugar futuro).
  const flags = featureFlagsState.value;
  const aiStudyVisible = flags.loaded ? flagEnabled("student.menu.aistudy", true) : true;
  // Gravador é experimental — default false. Só aparece se a flag estiver
  // ligada (allowlist contém o user, ou audience='all').
  const gravadorVisible = flags.loaded ? flagEnabled("student.menu.gravador", false) : false;
  const sharesVisible = flags.loaded ? flagEnabled("student.menu.shares", false) : false;
  // Itens gateados por flag — default escondidos pra todo mundo, exceto allowlist
  // (admins/testers). Pedido 2026-05-22: foco do aluno é Bancos + IA; demais
  // menus ficam ocultos por padrão. Enquanto flags carregam (flags.loaded=false),
  // assumimos escondido pra evitar flash de UI que vai sumir.
  const disciplinasVisible = flags.loaded ? flagEnabled("student.menu.disciplinas", false) : false;
  const provasVisible = flags.loaded ? flagEnabled("student.menu.provas", false) : false;
  const agendaVisible = flags.loaded ? flagEnabled("student.menu.agenda", false) : false;
  const inboxVisible = flags.loaded ? flagEnabled("student.menu.inbox", false) : false;
  const pagamentosVisible = flags.loaded ? flagEnabled("student.menu.pagamentos", false) : false;
  const navItems: NavItem[] = [
    { key: "home", label: "Início", icon: Home },
    ...(aiStudyVisible
      ? [
          { key: "aistudy" as const, label: "Estudando com IA", icon: Wand2 },
          // Atalho irmão pro Tira-dúvida — abre a MESMA tela (AiStudyView) já na
          // aba quickstudy. Antes só dava pra chegar pela aba tímida lá dentro.
          // [[project-tutor-ia-tira-duvida]]
          { key: "quickstudy" as const, label: "Tira-dúvida", icon: Sparkles }
        ]
      : []),
    { key: "conexoes", label: "Conexões", icon: Users },
    { key: "indicar", label: "Indicar amigos", icon: Gift },
    { key: "peerchat", label: "Chat", icon: MessageCircle },
    { key: "bancos", label: "Meu acervo de bancos", icon: Database },
    { key: "descobrir", label: "Biblioteca de provas", icon: Library },
    { key: "anotacoes", label: "Minhas anotações", icon: StickyNote },
    { key: "gruposestudo", label: "Grupos de Estudo", icon: Users },
    ...(gravadorVisible
      ? [{ key: "gravador" as const, label: "Gravador", icon: Mic }]
      : []),
    ...(sharesVisible
      ? [
          {
            key: "compartilhamentos" as const,
            label: "Compartilhamentos",
            icon: Share2
          }
        ]
      : []),
    // Item admin-only — gerenciamento rápido de turmas (grupos) sem precisar
    // sair do portal do aluno. Só aparece pra quem tem role admin na
    // plataforma. [[project-turmas-in-student-panel]]
    ...(isPlatformAdmin.value
      ? [{ key: "turmas" as const, label: "Turmas (admin)", icon: GraduationCap }]
      : []),
    ...(disciplinasVisible
      ? [{ key: "disciplinas" as const, label: "Disciplinas", icon: BookOpen }]
      : []),
    ...(provasVisible
      ? [
          {
            key: "provas" as const,
            label: "Provas",
            icon: FileText,
            badge: upcomingCount > 0 ? String(upcomingCount) : undefined
          }
        ]
      : []),
    ...(agendaVisible
      ? [{ key: "agenda" as const, label: "Agenda", icon: CalendarClock }]
      : []),
    // Item unificado — entra como `notificacoes` (canônico), mas mostra
    // ambas contagens (mensagens em âmbar, notificações em violeta). A view
    // destino é a InboxCenterView, que renderiza as duas listas lado a lado.
    // [[project-peer-chat-autoopen-toggle]] (unificação Notif+Mensagens)
    ...(inboxVisible
      ? [
          {
            key: "notificacoes" as const,
            label: "Caixa de entrada",
            icon: Inbox,
            // Ordem das badges espelha a ordem das colunas no InboxCenterView
            // (Notificações à esquerda, Mensagens à direita) — pedido 2026-05-20.
            badges: [
              ...(unreadNotifs > 0
                ? [
                    {
                      count: unreadNotifs,
                      color: "violet" as const,
                      title: `${unreadNotifs} notifica${unreadNotifs === 1 ? "ção" : "ções"} não-lida${unreadNotifs === 1 ? "" : "s"}`
                    }
                  ]
                : []),
              ...(unreadMessages > 0
                ? [
                    {
                      count: unreadMessages,
                      color: "amber" as const,
                      title: `${unreadMessages} mensagem${unreadMessages === 1 ? "" : "s"} da administração não-lida${unreadMessages === 1 ? "" : "s"}`
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    { key: "jogos", label: "Jogos", icon: Gamepad2 },
    // Plano + Cartões + Controle de gastos da IA + Assistente IA unificados
    // em uma única entrada (PagamentosView com 4 tabs). Aluno chega aqui pelo
    // nav key "assinatura" (default); os antigos "ia", "controlegastos" e
    // "formapagamento" ainda funcionam via deeplink mas não têm item de
    // sidebar próprio. [[project-payments-unification]]
    ...(pagamentosVisible
      ? [{ key: "assinatura" as const, label: "Pagamentos e consumo", icon: Wallet }]
      : []),
    { key: "meusreports", label: "Ajuda / Reportar", icon: LifeBuoy },
    ...(faqState.value.isEnabled
      ? [{ key: "faq" as const, label: "Perguntas frequentes", icon: HelpCircle }]
      : []),
    { key: "minhaconta", label: "Minha conta", icon: UserCircle }
  ];
  // Sinal recalcula ao mudar (sidebarOrder.value é lido aqui — Preact assina)
  void sidebarOrder.value;
  const orderedNavItems = applySidebarOrder(navItems);

  const isDark = themeSignal.value === "dark";
  // `collapsed` só vale no desktop. No mobile, sempre full (ícone + label).
  const collapsed = sidebarCollapsed.value && !isMobileViewport.value;
  const mobileOpen = sidebarMobileOpen.value;

  const widthClass = collapsed ? "md:w-16" : "md:w-72";
  // Mobile: drawer fixo, translate-x-full quando fechado
  const mobilePos = mobileOpen
    ? "translate-x-0"
    : "-translate-x-full md:translate-x-0";
  const sectionPadX = collapsed ? "px-2" : "px-4";

  function onNavClick(key: NavKey) {
    // Se estamos dentro de algum workspace (estudo, simulação, resultado),
    // sai antes de trocar de seção. Sem isso o nav mudava activeNav mas o
    // workspace continuava renderizado em cima.
    if (studyWorkspace.value) exitStudyWorkspace();
    if (provaWorkspace.value) exitProvaWorkspace();
    if (provaResultView.value) exitProvaResultView();
    if (provaRevisaoView.value) exitProvaRevisaoView();
    if (scheduledExamView.value) exitScheduledExamView();
    if (dissertativeWorkspace.value) exitDissertativeWorkspace();
    // Sai do workspace de Estudando com IA também, se estiver aberto.
    if (activeAiStudySessionId.value) {
      activeAiStudySessionId.value = null;
      activeAiStudySession.value = null;
    }
    // Clicar em "Bancos" na sidebar sempre leva pra LISTA, não pro último
    // banco aberto. Pra qualquer outra seção, também limpa o banco ativo
    // pra evitar "voltar" pro detalhe ao clicar Bancos depois.
    closeBankDetail();
    // "Estudando com IA" e "Tira-dúvida" são a mesma view (AiStudyView),
    // diferindo só pela aba ativa. Cada item fixa a aba certa antes de navegar.
    if (key === "aistudy") aiStudyTab.value = "cadernos";
    else if (key === "quickstudy") aiStudyTab.value = "quickstudy";
    setActiveNav(key);
    sidebarMobileOpen.value = false; // fecha drawer no mobile ao escolher
  }

  return (
    <>
      {/* Backdrop mobile */}
      {mobileOpen && (
        <div
          class="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => (sidebarMobileOpen.value = false)}
        />
      )}

      <aside
        class={`fixed left-0 top-0 z-40 flex h-screen w-72 flex-col border-r border-border bg-card backdrop-blur-md transition-[width,transform] duration-200 ease-out md:bg-card/50 ${widthClass} ${mobilePos}`}
      >
        {/* Logo + product + colapsar */}
        <div
          class={`flex items-center border-b border-border ${
            collapsed ? "justify-center px-2 py-5" : "gap-3 px-5 py-5"
          }`}
        >
          <div class="grid size-10 shrink-0 place-items-center rounded-md bg-brand-gradient text-white shadow-md shadow-primary/30 ring-1 ring-white/10">
            <UserRound
              className="size-5"
              strokeWidth={2.25}
              style={{ color: "#ffffff", stroke: "#ffffff" }}
            />
          </div>
          {!collapsed && (
            <>
              <div class="min-w-0 flex-1 leading-tight">
                <div class="truncate text-sm font-semibold">
                  {home.user.name || home.user.email}
                </div>
                <div class="text-[11px] text-muted-foreground">
                  Painel do aluno
                </div>
              </div>
              {/* Botão de colapsar — só desktop */}
              <button
                type="button"
                onClick={toggleSidebar}
                title="Recolher sidebar"
                aria-label="Recolher sidebar"
                class="hidden size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:grid"
              >
                <PanelLeftClose className="size-4" />
              </button>
              {/* Botão de fechar — só mobile */}
              <button
                type="button"
                onClick={() => (sidebarMobileOpen.value = false)}
                title="Fechar"
                aria-label="Fechar menu"
                class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
              >
                <X className="size-4" />
              </button>
            </>
          )}
        </div>

        {/* Botão de expandir quando colapsado (desktop) */}
        {collapsed && (
          <div class="hidden border-b border-border px-2 py-2 md:block">
            <button
              type="button"
              onClick={toggleSidebar}
              title="Expandir sidebar"
              aria-label="Expandir sidebar"
              class="grid h-9 w-full place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <PanelLeftOpen className="size-4" />
            </button>
          </div>
        )}

        {/* Middle: scrolla quando a viewport não comporta tudo (típico mobile) */}
        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          {/* User mini-card REMOVIDO 2026-05-19: modo aluno não tem switcher
              de organização, então mostrar a tag "Aluno · UNICID" só polui
              a sidebar. Aluno sempre vê tudo agregado (multi-company). */}

          {/* Nav */}
          <nav class={`flex-1 space-y-1 py-5 ${sectionPadX}`}>
            {sidebarEditMode.value && !collapsed && !isMobileViewport.value ? (
              <SortableNav
                items={orderedNavItems}
                onReorder={(next) =>
                  setSidebarOrder(next as SidebarKey[])
                }
              />
            ) : (
              orderedNavItems.map((item) => (
                <NavLink
                  key={item.key}
                  item={item}
                  isActive={effectiveNavKey() === item.key}
                  collapsed={collapsed}
                  onClick={() => onNavClick(item.key)}
                  activeKey={effectiveNavKey()}
                  onChildClick={(childKey) => onNavClick(childKey)}
                />
              ))
            )}
            {/* Toggle edit mode — só desktop expandido */}
            {!collapsed && !isMobileViewport.value && (
              <div class="mt-3 border-t border-border/60 pt-3">
                {sidebarEditMode.value ? (
                  <div class="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => (sidebarEditMode.value = false)}
                      class="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
                    >
                      <Check className="size-3.5" />
                      Concluir
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        resetSidebarOrder();
                      }}
                      class="text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      Restaurar ordem padrão
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => (sidebarEditMode.value = true)}
                    title="Arraste os itens pra reordenar o menu"
                    class="inline-flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <ArrowDownUp className="size-3.5" />
                    Reordenar menu
                  </button>
                )}
              </div>
            )}
          </nav>

        </div>

        {/* Doação Pix — FIXA acima do footer, fora da área de scroll. Antes
            ficava dentro do scrolling container e desaparecia ao rolar.
            Pedido 2026-05-19: scroll do menu termina ANTES da doação. */}
        {!collapsed && (
          <div class={`shrink-0 border-t border-border py-3 ${sectionPadX}`}>
            <DonationCard />
          </div>
        )}

        {/* Footer ações */}
        <div class={`space-y-1 border-t border-border py-4 ${sectionPadX}`}>
          <button
            type="button"
            onClick={() => (guiaOpen.value = true)}
            title={collapsed ? "Guia do usuário" : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed
                ? "justify-center px-2 py-2.5"
                : "gap-3 px-3 py-2.5"
            }`}
          >
            <HelpCircle width="1.3em" height="1.3em" />
            {!collapsed && <span>Guia do usuário</span>}
          </button>
          <button
            type="button"
            onClick={() => (prefsOpen.value = true)}
            title={collapsed ? "Preferências" : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed
                ? "justify-center px-2 py-2.5"
                : "gap-3 px-3 py-2.5"
            }`}
          >
            <Settings width="1.3em" height="1.3em" />
            {!collapsed && <span>Preferências</span>}
          </button>
          <button
            type="button"
            onClick={() => toggleTheme()}
            title={isDark ? "Tema claro (Ctrl+/)" : "Tema escuro (Ctrl+/)"}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed
                ? "justify-center px-2 py-2.5"
                : "gap-3 px-3 py-2.5"
            }`}
          >
            {isDark ? <Sun width="1.3em" height="1.3em" /> : <Moon width="1.3em" height="1.3em" />}
            {!collapsed && <span>Tema {isDark ? "claro" : "escuro"}</span>}
          </button>
          {hasNonStudentProfile.value && (
            <button
              type="button"
              onClick={goSwitch}
              title={collapsed ? "Trocar perfil" : undefined}
              class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
                collapsed
                  ? "justify-center px-2 py-2.5"
                  : "gap-3 px-3 py-2.5"
              }`}
            >
              <ArrowLeftRight width="1.3em" height="1.3em" />
              {!collapsed && <span>Trocar perfil</span>}
            </button>
          )}
          <button
            type="button"
            onClick={handleLogout}
            title={collapsed ? "Sair" : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive ${
              collapsed
                ? "justify-center px-2 py-2.5"
                : "gap-3 px-3 py-2.5"
            }`}
          >
            <LogOut width="1.3em" height="1.3em" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

// =============================================================================
// Mesh + Cards do conteúdo
// =============================================================================

function MeshBackground() {
  // Página clean — sem gradient violeta de fundo
  return null;
}

function UpcomingCard({ a }: { a: UpcomingAssessment }) {
  const running = a.status === "running";
  const startedAttempt = !!a.attemptStartedAt && !a.attemptSubmittedAt;
  const submitted = !!a.attemptSubmittedAt;
  const countdown = timeUntil(a.startsAt);
  const Icon = a.kind === "trabalho" ? FileText : PlayCircle;
  return (
    <Card className="border-border/70 bg-card/70 transition-colors hover:border-primary/50">
      <CardContent className="flex items-start gap-4 p-6">
        <div
          class={`grid size-11 shrink-0 place-items-center rounded-md ring-1 ${
            running
              ? "bg-primary text-primary-foreground ring-primary/30 shadow-md shadow-primary/40"
              : "bg-primary/10 text-primary ring-primary/20"
          }`}
        >
          <Icon className="size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-start gap-2">
            <div class="font-semibold leading-tight">{a.title}</div>
            {running && (
              <Badge className="border-0 bg-emerald-500 text-white shrink-0">
                Acontecendo
              </Badge>
            )}
            {submitted && (
              <Badge
                variant="outline"
                className="shrink-0 border-emerald-500/40 text-emerald-500"
              >
                <CheckCircle2 className="mr-1 size-3" /> Enviada
              </Badge>
            )}
          </div>
          <div class="mt-1 text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {kindLabel[a.kind]}
            </Badge>
            <Clock width="1.3em" height="1.3em" />
            <span>{formatDateTime(a.startsAt)}</span>
            {a.durationMinutes && <span>· {a.durationMinutes} min</span>}
            {countdown && !running && (
              <span class="text-primary font-medium">· {countdown}</span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          className={
            running
              ? "bg-brand-gradient border-0 text-white shadow-md shadow-primary/40 hover:brightness-110"
              : ""
          }
          variant={running ? "default" : "outline"}
          disabled={!running && !startedAttempt}
        >
          {running ? "Entrar" : startedAttempt ? "Continuar" : "Ver detalhes"}
        </Button>
      </CardContent>
    </Card>
  );
}

function UpcomingSection({ list }: { list: UpcomingAssessment[] }) {
  if (list.length === 0) {
    return (
      <Card className="border-dashed border-border bg-card/40">
        <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
          <CalendarClock className="size-5 text-muted-foreground/70" />
          Nenhuma avaliação agendada no momento.
        </CardContent>
      </Card>
    );
  }
  return (
    <div class="space-y-3">
      {list.map((a) => (
        <UpcomingCard key={a.id} a={a} />
      ))}
    </div>
  );
}

function OfferingsSection({ list }: { list: OfferingRow[] }) {
  if (list.length === 0) {
    return (
      <Card className="max-w-md border-dashed border-border bg-card/40">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Nenhuma disciplina vinculada às suas turmas ainda.
        </CardContent>
      </Card>
    );
  }
  return (
    <div
      class="grid gap-3"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 400px))" }}
    >
      {list.map((o) => (
        <Card
          key={o.offeringId}
          className="border-border/70 bg-card/70 transition-colors hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10"
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-start justify-between gap-2 text-base">
              <span class="leading-tight">{o.disciplinaName}</span>
              {o.disciplinaStatus !== "active" && (
                <Badge variant="outline" className="text-[10px]">
                  {o.disciplinaStatus}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="flex items-center gap-1.5 text-xs">
              <Users width="1.3em" height="1.3em" className="shrink-0" />
              <span class="truncate">
                {o.turmaName}
                {o.disciplinaCode && (
                  <span class="text-muted-foreground/60"> · {o.disciplinaCode}</span>
                )}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Button variant="outline" size="sm" className="w-full">
              <BookMarked width="1.3em" height="1.3em" />
              <span>Abrir disciplina</span>
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TurmasStrip({ list }: { list: TurmaMin[] }) {
  if (list.length === 0) return null;
  return (
    <div
      class="grid gap-3"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 320px))" }}
    >
      {list.map((t) => (
        <Card
          key={t.id}
          className="border-border/70 bg-card/70 transition-colors hover:border-primary/50"
        >
          <CardContent className="flex items-center gap-3 p-4">
            <div class="grid size-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
              <Users className="size-4" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-semibold">{t.name}</div>
              {t.code && (
                <div class="truncate text-xs text-muted-foreground">{t.code}</div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// Views (por NavKey)
// =============================================================================

function HomeView({ home }: { home: StudentHomeResponse }) {
  const firstName = (home.user.name || "").trim().split(/\s+/)[0] || "";

  // Subscribe ao signal de layout pra reagir a mudanças
  const layout = homeLayout.value;
  const editing = homeEditMode.value;

  // Boas-vindas: abre AUTOMATICAMENTE só no primeiro acesso real do aluno.
  // A função maybeOpenWelcomeFirstTime checa localStorage (rápido + sem race)
  // antes de bater no server. Re-mount da home depois do "seen" NÃO reabre.
  // Decisão 2026-05-23. [[project-welcome-modal]]
  useEffect(() => {
    void maybeOpenWelcomeFirstTime();
  }, []);

  // Mapeia cada HomeBlockId pro JSX correspondente. Cada bloco é atômico —
  // pode ser arrastado independentemente pra qualquer célula da matriz 2D.
  const blockContent: Record<HomeBlockId, () => any> = {
    notifications: () => (
      <NotificationsHomeCard onOpenAll={() => setActiveNav("notificacoes")} />
    ),
    eventos: () => (
      <ProximosEventosCard onOpenAll={() => setActiveNav("agenda")} />
    ),
    mensagens: () => (
      <MensagensHomeCard onOpenAll={() => setActiveNav("mensagens")} />
    ),
    aiStudy: () => <AiStudyHomeCard onOpen={() => setActiveNav("aistudy")} />,
    pagamentos: () => (
      <PagamentosHomeCard onOpenAll={() => setActiveNav("disciplinas")} />
    ),
    destaque: () => (
      <DestaqueHomeCard
        home={home}
        onOpenItem={(a) => {
          if (a.status === "running" || a.status === "scheduled") {
            setActiveNav("provas");
          }
        }}
        onOpenProvaAtiva={(p) => {
          setActiveNav("bancos");
          openBank(p.bankId);
        }}
      />
    ),
    simulacoes: () => (
      <SimulacoesRecentesCard
        home={home}
        onOpenAll={() => setActiveNav("bancos")}
        onOpenBank={(bankId) => {
          setActiveNav("bancos");
          openBank(bankId);
        }}
      />
    ),
    gruposEstudo: () => (
      <GruposEstudoHomeCard
        onOpen={() => setActiveNav("gruposestudo")}
        onOpenGroup={(id) => {
          setActiveNav("gruposestudo");
          openStudyGroup(id);
        }}
      />
    ),
    banks: () => (
      <section>
        <div class="mb-3 flex items-baseline justify-between gap-3">
          <h2 class="text-lg font-semibold tracking-tight">
            Meu acervo de bancos
          </h2>
          {home.totals.banks > 8 && (
            <button
              type="button"
              onClick={() => setActiveNav("bancos")}
              class="text-xs font-medium text-primary hover:underline"
            >
              Ver todos ({home.totals.banks})
            </button>
          )}
        </div>
        <BanksGrid list={home.banks.slice(0, 8)} />
      </section>
    ),
    turmasDisciplinas: () => <TurmasDisciplinasBlock home={home} />
  };

  return (
    <div class="space-y-8">
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <h1 class="text-2xl font-extrabold tracking-tight md:text-3xl">
              Olá{firstName ? `, ${firstName}` : ""} 👋
            </h1>
            <button
              type="button"
              onClick={openWelcome}
              title="Tour guiado pela plataforma — bancos, modos de estudo, redações, IA"
              class="inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-primary to-fuchsia-600 px-4 py-2 text-sm font-bold text-primary-foreground shadow-sm transition-all hover:brightness-110 hover:shadow-md"
            >
              <Sparkles className="size-4" />
              Por onde começar?
            </button>
          </div>
          <p class="mt-1 text-sm text-muted-foreground">
            Aqui está o que você pode retomar agora.
          </p>
        </div>
        <HomeLayoutButton editing={editing} />
      </header>

      {editing && <HomeLayoutEditor />}

      {/* Recado da plataforma — fixo entre saudação e grid customizável.
          Não entra no layout drag-and-drop pra garantir visibilidade. */}
      <AnnouncementBanner />

      {/* Tutor IA · Tira-dúvida — hero da home, fixo no topo (estrela da
          plataforma). Substituiu o QuickStudyCard antigo. O Card continua
          exportado pra ser usado em outros lugares se preciso.
          [[project-quickstudy]] [[project-tutor-ia-hero]] */}
      <TutorIaHero
        onOpenFull={() => {
          aiStudyTab.value = "quickstudy";
          setActiveNav("aistudy");
        }}
      />

      <HomeBlocksContainer
        rows={layout.rows}
        editing={editing}
        renderBlock={(id) => blockContent[id]()}
      />
    </div>
  );
}

interface AnnouncementPayload {
  title: string;
  text: string;
  tone: string;
  enabledAt: number | null;
}

const announcementSignal = signal<AnnouncementPayload | null | "loading">(
  "loading"
);
const announcementDismissedAt = signal<number>(0);

function dismissKey(enabledAt: number | null): string {
  return `student.announcement.dismiss.${enabledAt ?? 0}`;
}

function loadAnnouncementDismiss(enabledAt: number | null): boolean {
  try {
    const v = localStorage.getItem(dismissKey(enabledAt));
    return v === "1";
  } catch {
    return false;
  }
}

function saveAnnouncementDismiss(enabledAt: number | null): void {
  try {
    localStorage.setItem(dismissKey(enabledAt), "1");
  } catch {
    /* ignore */
  }
}

async function fetchAnnouncement(): Promise<void> {
  try {
    const res = await fetch("/api/student/announcement", {
      credentials: "include"
    });
    if (!res.ok) {
      announcementSignal.value = null;
      return;
    }
    const data = (await res.json()) as { announcement: AnnouncementPayload | null };
    announcementSignal.value = data.announcement ?? null;
  } catch {
    announcementSignal.value = null;
  }
}

function AnnouncementBanner() {
  const value = announcementSignal.value;
  // Carrega 1x ao montar
  useEffect(() => {
    if (value === "loading") void fetchAnnouncement();
  }, []);
  if (value === "loading" || value === null) return null;
  if (!value.title.trim() && !value.text.trim()) return null;
  const dismissed = loadAnnouncementDismiss(value.enabledAt);
  if (dismissed && announcementDismissedAt.value === (value.enabledAt ?? 0)) {
    return null;
  }
  // Mapeia tone → cor
  const toneStyles: Record<string, { bg: string; border: string; text: string; iconColor: string }> = {
    info: {
      bg: "bg-blue-500/10",
      border: "border-blue-500/30",
      text: "text-blue-900 dark:text-blue-100",
      iconColor: "text-blue-600 dark:text-blue-400"
    },
    success: {
      bg: "bg-emerald-500/10",
      border: "border-emerald-500/30",
      text: "text-emerald-900 dark:text-emerald-100",
      iconColor: "text-emerald-600 dark:text-emerald-400"
    },
    warning: {
      bg: "bg-amber-500/10",
      border: "border-amber-500/40",
      text: "text-amber-900 dark:text-amber-100",
      iconColor: "text-amber-600 dark:text-amber-400"
    },
    danger: {
      bg: "bg-red-500/10",
      border: "border-red-500/40",
      text: "text-red-900 dark:text-red-100",
      iconColor: "text-red-600 dark:text-red-400"
    },
    neutral: {
      bg: "bg-muted/40",
      border: "border-border",
      text: "text-foreground",
      iconColor: "text-muted-foreground"
    }
  };
  const t = toneStyles[value.tone] ?? toneStyles.info;
  return (
    <section
      class={`flex items-start gap-3 rounded-md border px-4 py-3 ${t.bg} ${t.border}`}
    >
      <Megaphone className={`mt-0.5 size-5 shrink-0 ${t.iconColor}`} />
      <div class={`min-w-0 flex-1 ${t.text}`}>
        {value.title.trim() && (
          <h2 class="text-sm font-bold leading-tight">{value.title}</h2>
        )}
        {value.text.trim() && (
          <p class={`whitespace-pre-line text-sm leading-relaxed ${value.title.trim() ? "mt-1" : ""}`}>
            {value.text}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          saveAnnouncementDismiss(value.enabledAt);
          announcementDismissedAt.value = value.enabledAt ?? 0;
        }}
        title="Dispensar"
        aria-label="Dispensar"
        class={`shrink-0 rounded-md p-1 opacity-70 hover:bg-background/40 hover:opacity-100 ${t.iconColor}`}
      >
        <X className="size-4" />
      </button>
    </section>
  );
}

function TurmasDisciplinasBlock({ home }: { home: StudentHomeResponse }) {
  return (
    <section class="grid gap-6 md:grid-cols-2">
        <div>
          <h2 class="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Suas turmas
          </h2>
          {home.turmas.length === 0 ? (
            <p class="text-sm text-muted-foreground">
              Você não está matriculado em nenhuma turma desta Organização.
            </p>
          ) : (
            <ul class="space-y-1.5">
              {home.turmas.map((t) => (
                <li
                  key={t.id}
                  class="flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2 text-sm"
                >
                  <Users width="1.3em" height="1.3em" className="text-primary shrink-0" />
                  <span class="truncate font-medium">{t.name}</span>
                  {t.code && (
                    <span class="ml-auto shrink-0 text-xs text-muted-foreground">
                      {t.code}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h2 class="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Disciplinas
          </h2>
          {home.offerings.length === 0 ? (
            <p class="text-sm text-muted-foreground">
              Nenhuma disciplina vinculada às suas turmas ainda.
            </p>
          ) : (
            <ul class="space-y-1.5">
              {home.offerings.map((o) => (
                <li
                  key={o.offeringId}
                  class="flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2 text-sm"
                >
                  <BookMarked width="1.3em" height="1.3em" className="text-primary shrink-0" />
                  <span class="truncate font-medium">{o.disciplinaName}</span>
                  <span class="ml-auto shrink-0 text-xs text-muted-foreground">
                    {o.turmaName}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
  );
}

function HomeLayoutButton({ editing }: { editing: boolean }) {
  return (
    <button
      type="button"
      onClick={() => (homeEditMode.value = !editing)}
      title={editing ? "Sair da edição de layout" : "Editar layout da home"}
      class={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
        editing
          ? "border-primary/60 bg-primary/10 text-primary"
          : "border-border bg-background text-muted-foreground hover:bg-accent"
      }`}
    >
      <Settings2 className="size-3.5" />
      {editing ? "Concluir" : "Editar layout"}
    </button>
  );
}

function HomeLayoutEditor() {
  const layout = homeLayout.value;
  const hiddenSet = new Set(layout.hidden);
  return (
    <section class="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-4">
      <header class="flex items-center justify-between gap-3">
        <h2 class="text-[11px] font-bold uppercase tracking-wider text-primary">
          Customizar home
        </h2>
        <button
          type="button"
          onClick={() => resetHomeLayout()}
          class="text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:underline"
        >
          Restaurar padrão
        </button>
      </header>
      <p class="text-xs text-muted-foreground">
        Marque o que mostrar. No desktop, arraste pela alça
        <GripVertical className="mx-0.5 inline size-3 align-text-bottom" />
        pra reordenar dentro de uma linha OU entre linhas — cada linha pode ter
        quantos cards você quiser (1, 2, 3…).
      </p>

      <div>
        <button
          type="button"
          onClick={() => appendEmptyRow()}
          class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-background px-3 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
        >
          <Plus className="size-3" />
          Adicionar linha vazia
        </button>
      </div>

      <div>
        <div class="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Visibilidade
        </div>
        <ul class="grid gap-1.5 sm:grid-cols-2">
          {ALL_HOME_BLOCKS.map((b) => {
            const visible = !hiddenSet.has(b.id);
            return (
              <li key={b.id}>
                <label class="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2 text-sm hover:bg-accent/40">
                  <Switch
                    checked={visible}
                    onCheckedChange={() => toggleHomeBlock(b.id)}
                  />
                  <span class="flex-1">{b.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

// Container que renderiza linhas independentes — cada linha tem suas próprias
// colunas (1 ~ N). Desktop+edição: D&D dentro da linha E entre linhas usando
// múltiplos SortableContext sob um único DndContext. Mobile: empilha tudo em
// 1 coluna (linhas viram blocos sequenciais).
function HomeBlocksContainer({
  rows,
  editing,
  renderBlock
}: {
  rows: HomeBlockId[][];
  editing: boolean;
  renderBlock: (id: HomeBlockId) => any;
}) {
  const [isDesktop, setIsDesktop] = useState(() => isDesktopViewport());
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  // ID do bloco sendo arrastado — usado pelo DragOverlay (clone que segue o
  // cursor, fora do fluxo do grid) pra dar feedback visual claro.
  const [activeDragId, setActiveDragId] = useState<HomeBlockId | null>(null);
  // ID do que está sob o cursor — pode ser bloco ou "row-empty-N". Usado pelo
  // RowDropZone pra destacar a linha alvo enquanto arrasta.
  const [overId, setOverId] = useState<string | null>(null);

  function onDragStart(e: DragStartEvent) {
    setActiveDragId(String(e.active.id) as HomeBlockId);
  }

  function onDragOver(e: { over: { id: string | number } | null }) {
    setOverId(e.over ? String(e.over.id) : null);
  }

  // Grid de 6 colunas no desktop; cada bloco ocupa um span (2/3/4/6) baseado
  // no size escolhido pelo usuário. Mobile (sm-): 1 coluna empilhada — span
  // é ignorado via col-span-full no mobile.
  const rowGridClass = "grid grid-cols-1 gap-4 md:grid-cols-6";

  function blockSpanClass(id: HomeBlockId): string {
    const span = getBlockSpan(id);
    // No mobile sempre col-span-full (1 coluna). No desktop usa o span do size.
    const desktop: Record<number, string> = {
      2: "md:col-span-2",
      3: "md:col-span-3",
      4: "md:col-span-4",
      6: "md:col-span-6"
    };
    return `col-span-1 ${desktop[span] ?? "md:col-span-3"}`;
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveDragId(null);
    setOverId(null);
    const { active, over } = e;
    if (!over) return;
    const activeId = String(active.id) as HomeBlockId;
    const overIdRaw = String(over.id);

    // Drop em "row-empty-N" → mover pra linha N (no fim dela)
    if (overIdRaw.startsWith("row-empty-")) {
      const rowIdx = Number(overIdRaw.slice("row-empty-".length));
      if (Number.isFinite(rowIdx)) {
        moveHomeBlock(activeId, rowIdx, rows[rowIdx]?.length ?? 0);
      }
      return;
    }

    const overId = overIdRaw as HomeBlockId;
    if (activeId === overId) return;

    // Encontra posição do over (qual linha + índice)
    let toRow = -1;
    let toCol = -1;
    for (let r = 0; r < rows.length; r++) {
      const c = rows[r].indexOf(overId);
      if (c >= 0) {
        toRow = r;
        toCol = c;
        break;
      }
    }
    if (toRow < 0) return;
    moveHomeBlock(activeId, toRow, toCol);
  }

  // Modo leitura ou mobile: render simples linha por linha. No mobile cada
  // bloco fica em 1 coluna full-width (col-span-1 sobre grid-cols-1); no
  // desktop respeita o size escolhido (col-span-2/3/4/6 do grid de 6).
  if (!editing || !isDesktop) {
    return (
      <div class="space-y-4 md:space-y-6">
        {rows.map((row, i) => (
          <div key={`row-${i}`} class={rowGridClass}>
            {row.map((id) => (
              <div key={id} class={`min-w-0 ${blockSpanClass(id)}`}>
                {renderBlock(id)}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Quais linhas estão "ativas" (o cursor está sobre algum bloco delas ou
  // sobre a row-empty marker). Calcula em cada render durante o drag pra
  // dar feedback visual reforçado.
  const activeRowIdx: number = (() => {
    if (!overId) return -1;
    if (overId.startsWith("row-empty-")) {
      return Number(overId.slice("row-empty-".length));
    }
    for (let r = 0; r < rows.length; r++) {
      if (rows[r].includes(overId as HomeBlockId)) return r;
    }
    return -1;
  })();

  // Bloco sendo arrastado (pra preview do DragOverlay)
  const dragBlock = activeDragId
    ? <DragGhost id={activeDragId}>{renderBlock(activeDragId)}</DragGhost>
    : null;

  // Modo edição desktop: um SortableContext por linha + um droppable extra
  // pra "drop aqui pra criar linha nova". DragOverlay mostra clone do bloco
  // seguindo o cursor (fora do fluxo do grid) — feedback visual claro.
  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActiveDragId(null);
        setOverId(null);
      }}
    >
      <div class="space-y-3">
        {rows.map((row, i) => (
          <RowDropZone
            key={`row-${i}`}
            rowIdx={i}
            ids={row}
            isActive={activeRowIdx === i && activeDragId !== null}
          >
            <SortableContext items={row} strategy={rectSortingStrategy}>
              <div class={rowGridClass}>
                {row.map((id) => (
                  <SortableBlock
                    key={id}
                    id={id}
                    fullWidth={isFullWidthBlock(id)}
                  >
                    {renderBlock(id)}
                  </SortableBlock>
                ))}
                {row.length === 0 && (
                  <div class="col-span-1 md:col-span-6 rounded-md border border-dashed border-primary/40 bg-primary/5 px-4 py-6 text-center text-[11px] text-muted-foreground">
                    Linha vazia — arraste um bloco aqui
                  </div>
                )}
              </div>
            </SortableContext>
          </RowDropZone>
        ))}
        {/* Drop zone pra criar linha nova no fim */}
        <RowDropZone
          rowIdx={rows.length}
          ids={[]}
          variant="new"
          isActive={activeRowIdx === rows.length && activeDragId !== null}
        >
          <div class="rounded-md border-2 border-dashed border-border bg-muted/30 px-4 py-3 text-center text-[11px] text-muted-foreground transition-colors">
            Solte aqui pra criar uma linha nova
          </div>
        </RowDropZone>
      </div>
      <DragOverlay dropAnimation={{ duration: 180 }}>
        {dragBlock}
      </DragOverlay>
    </DndContext>
  );
}

/** Clone visual do bloco sendo arrastado, mostrado pelo DragOverlay. Reduzido
 *  + rotacionado pra parecer "levantado" e dar dica que está em movimento. */
function DragGhost({ id, children }: { id: HomeBlockId; children: any }) {
  return (
    <div
      style={{
        width: "260px",
        transform: "rotate(-2deg) scale(0.92)",
        opacity: 0.95,
        pointerEvents: "none"
      }}
      class="rounded-md border-2 border-primary bg-card shadow-2xl ring-4 ring-primary/30"
    >
      {/* Header de "carregando" pra o conteúdo não ficar arrastando junto */}
      <div class="flex items-center gap-2 border-b border-border bg-primary/10 px-3 py-1.5 text-[11px] font-semibold text-primary">
        <GripVertical className="size-3" />
        Movendo bloco
      </div>
      <div class="max-h-32 overflow-hidden p-2 opacity-80">{children}</div>
    </div>
  );
}

function RowDropZone({
  rowIdx,
  ids,
  variant,
  isActive,
  children
}: {
  rowIdx: number;
  ids: HomeBlockId[];
  variant?: "new";
  isActive?: boolean;
  children: any;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `row-empty-${rowIdx}`,
    data: { rowIdx, ids }
  });
  // "Highlight" = quando o cursor está sobre o droppable interno OU sobre
  // qualquer bloco da linha (que vem via prop isActive do pai).
  const highlight = isOver || isActive;
  return (
    <div
      ref={setNodeRef}
      class={
        variant === "new"
          ? `rounded-md transition-all ${
              highlight
                ? "scale-[1.01] bg-primary/10 ring-2 ring-primary/60"
                : ""
            }`
          : `relative rounded-lg p-1 transition-all ${
              highlight
                ? "bg-primary/5 ring-2 ring-primary/60 ring-offset-2 ring-offset-background"
                : ""
            }`
      }
    >
      {variant !== "new" && highlight && (
        <div class="pointer-events-none absolute -top-2.5 left-3 z-20 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground shadow-md">
          Linha {rowIdx + 1}
        </div>
      )}
      {children}
    </div>
  );
}

function SortableBlock({
  id,
  fullWidth,
  children
}: {
  id: HomeBlockId;
  fullWidth?: boolean;
  children: any;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });
  const span = getBlockSpan(id);
  const desktopSpanClass: Record<number, string> = {
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
    6: "md:col-span-6"
  };
  const spanClass = fullWidth
    ? "col-span-1 md:col-span-6"
    : `col-span-1 ${desktopSpanClass[span] ?? "md:col-span-3"}`;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      class={`relative min-w-0 rounded-md ring-2 ring-primary/30 ring-offset-2 ring-offset-background ${spanClass} ${
        isDragging ? "z-10 cursor-grabbing" : ""
      }`}
    >
      <button
        type="button"
        {...(attributes as any)}
        {...(listeners as any)}
        title="Arrastar para reordenar"
        aria-label="Arrastar para reordenar"
        class="absolute -left-3 top-1/2 z-10 grid size-7 -translate-y-1/2 cursor-grab place-items-center rounded-md border border-primary/40 bg-primary text-primary-foreground shadow-md hover:scale-105 active:cursor-grabbing"
      >
        <GripVertical className="size-3.5" />
      </button>
      {!fullWidth && <SizeBadge id={id} />}
      {children}
    </div>
  );
}

/** Pílula com 4 botões de tamanho (S/M/L/XL) no canto superior direito do
 *  bloco. Cada clique muda o span do bloco no grid de 6 colunas. */
function SizeBadge({ id }: { id: HomeBlockId }) {
  const current = getBlockSize(id);
  const options: Array<{ size: BlockSize; label: string; title: string }> = [
    { size: "sm", label: "S", title: "Pequeno (1/3 da linha)" },
    { size: "md", label: "M", title: "Médio (1/2 da linha)" },
    { size: "lg", label: "L", title: "Grande (2/3 da linha)" },
    { size: "xl", label: "XL", title: "Linha inteira" }
  ];
  return (
    <div
      class="absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-md border border-primary/40 bg-background/95 p-0.5 text-[10px] shadow-md backdrop-blur"
      onPointerDown={(e) => e.stopPropagation()}
    >
      {options.map((o) => (
        <button
          key={o.size}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setHomeBlockSize(id, o.size);
          }}
          title={o.title}
          aria-label={o.title}
          class={`grid size-5 place-items-center rounded font-bold transition-colors ${
            current === o.size
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Card para prova em andamento (não-agendada, vinda de um banco qualquer).
// Visual: âmbar suave + ícone PlayCircle, contador respondidas/total.
function ProvaAtivaCard({ prova }: { prova: ProvaWithBank }) {
  const pct = prova.total > 0 ? Math.round((prova.respondidas / prova.total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={() => {
        // Prova em andamento: vai DIRETO pro workspace, sem passar pela
        // tela de detalhe do banco. Se o aluno depois clicar no nome do
        // banco no breadcrumb, aí sim cai no detalhe.
        setActiveNav("bancos");
        enterProvaWorkspace(prova.bankId);
      }}
      class="group flex w-full items-center gap-4 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3.5 text-left transition-colors hover:border-amber-500/60 hover:bg-amber-500/10"
    >
      <div class="grid size-11 shrink-0 place-items-center rounded-md bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/30 dark:text-amber-400">
        <PlayCircle className="size-5" />
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="inline-flex size-2 shrink-0 rounded-full bg-amber-500 ring-2 ring-amber-500/30 animate-pulse"
            title="Prova em andamento"
            aria-label="Prova em andamento"
          />
          <MarqueeOnHover className="min-w-0 flex-1 text-sm font-bold">
            {prova.bankName}
          </MarqueeOnHover>
        </div>
        <div class="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            <span class="font-semibold text-foreground">{prova.respondidas}</span>
            /{prova.total} respondidas
          </span>
          <span class="opacity-50">·</span>
          <span>{pct}%</span>
          {prova.iniciadaEm && (
            <>
              <span class="opacity-50">·</span>
              <Clock width="1.3em" height="1.3em" />
              <span>iniciada {formatDateTime(new Date(prova.iniciadaEm).toISOString())}</span>
            </>
          )}
        </div>
      </div>
      <div class="shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5">
        ›
      </div>
    </button>
  );
}

// Grid de bancos resumidos pra home. Cards um pouco mais compactos que a
// view dedicada — foco em retomar (estudo) sem mostrar toda a metadata.
function BanksGrid({ list }: { list: HomeBank[] }) {
  if (list.length === 0) {
    return (
      <div class="rounded-md border border-dashed border-border bg-card/40 px-6 py-8 text-center text-sm text-muted-foreground">
        Nenhum banco disponível ainda. Quando seu professor liberar, ele aparece aqui.
      </div>
    );
  }
  // Cap em 4 colunas no desktop (2 linhas de 4 = 8 cards totais).
  // Mobile/tablet seguem responsivos pra não comprimir.
  return (
    <div class="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {list.map((b) => (
        <BankMiniCard key={b.id} bank={b} />
      ))}
    </div>
  );
}

// Badges compactos mostrando os tipos de questão que o banco tem.
// "ME" = múltipla escolha, "Dissertativa" = essay. Aparece só quando o
// backend informou os counts e o banco tem questões.
function BankKindBadges({
  bank
}: {
  bank: {
    questionKinds?: Array<"mc" | "essay">;
    questionKindCounts?: { mc: number; essay: number };
    dissertativeCount?: number;
  };
}) {
  const kinds = bank.questionKinds;
  const counts = bank.questionKindCounts;
  const aiDiss = bank.dissertativeCount ?? 0;
  if ((!kinds || kinds.length === 0) && aiDiss === 0) return null;
  const hasMc = kinds?.includes("mc") ?? false;
  const hasEssay = kinds?.includes("essay") ?? false;
  // Sempre mostramos o número, mesmo em banco monotipo — o aluno gosta
  // de ver no card "12 ME / 4 DISS" sem precisar abrir o detalhe.
  const mcCount = counts?.mc ?? 0;
  const essayCount = counts?.essay ?? 0;
  return (
    <span class="flex shrink-0 items-center gap-1">
      <span aria-hidden="true">·</span>
      {hasMc && (
        <span
          class="rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300"
          title={`${mcCount} de múltipla escolha`}
        >
          {mcCount} ME
        </span>
      )}
      {hasEssay && (
        <span
          class="rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300"
          title={`${essayCount} dissertativa${essayCount === 1 ? "" : "s"} (tipo individual)`}
        >
          {essayCount} DISS
        </span>
      )}
      {aiDiss > 0 && (
        <span
          class="inline-flex items-center gap-0.5 rounded-md bg-fuchsia-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-fuchsia-700 ring-1 ring-fuchsia-500/30 dark:text-fuchsia-300"
          title={`Banco possui versão dissertativa gerada por IA: ${aiDiss} questão${aiDiss !== 1 ? "ões" : ""}`}
        >
          <Sparkles width="0.7em" height="0.7em" />
          IA Disc.
        </span>
      )}
    </span>
  );
}

export function BankMiniCard({ bank }: { bank: HomeBank }) {
  const pct = bank.estudo.accuracyPct;
  const respondidasPct =
    bank.questionCount > 0
      ? Math.round((bank.estudo.respondidas / bank.questionCount) * 100)
      : 0;
  return (
    <button
      type="button"
      onClick={() => {
        setActiveNav("bancos");
        openBank(bank.id);
      }}
      class="group flex flex-col gap-3 rounded-md border border-border bg-card/60 p-4 text-left transition-colors hover:border-primary/50 hover:bg-card"
    >
      <div class="flex items-start gap-3">
        <div class="grid size-10 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
          <Database className="size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <MarqueeOnHover className="text-sm font-bold">{bank.name}</MarqueeOnHover>
          <div class="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>
              {bank.questionCount} {bank.questionCount === 1 ? "questão" : "questões"}
            </span>
            <BankKindBadges bank={bank} />
            {bank.sharedByName && (
              <span
                class="inline-flex items-center gap-0.5 rounded-md border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-300"
                title={`Compartilhado por ${bank.sharedByName}`}
              >
                <Share2 className="size-2.5" />
                por {bank.sharedByName.split(" ")[0]}
              </span>
            )}
          </div>
        </div>
        {bank.provas.emAndamento && (
          <span
            class="inline-flex shrink-0 items-center gap-1 self-start rounded-full border border-amber-500/50 bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 shadow-sm shadow-amber-500/20 ring-1 ring-amber-500/40 dark:text-amber-300"
            title="Prova em andamento — toque para retomar"
            aria-label="Prova em andamento"
          >
            <span class="relative flex size-1.5">
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
              <span class="relative inline-flex size-1.5 rounded-full bg-amber-500" />
            </span>
            <PlayCircle className="size-3" strokeWidth={2.5} />
            <span class="hidden sm:inline">Em andamento</span>
          </span>
        )}
      </div>
      {/* Barra de progresso (respondidas) */}
      <div class="space-y-1.5">
        <div class="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            class="h-full rounded-full bg-primary transition-all"
            style={{ width: `${respondidasPct}%` }}
          />
        </div>
        <div class="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>
            <span class="font-semibold text-foreground">{bank.estudo.respondidas}</span>
            /{bank.questionCount} respondidas
          </span>
          {pct != null && (
            <span class="font-semibold text-primary">{pct}% acerto</span>
          )}
        </div>
      </div>
      {bank.provas.total > 0 && (
        <div class="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <ClipboardCheck width="1.3em" height="1.3em" />
          <span>
            {bank.provas.total} prova{bank.provas.total > 1 ? "s" : ""} no histórico
            {bank.provas.melhorPercentual != null && (
              <> · melhor {bank.provas.melhorPercentual}%</>
            )}
          </span>
        </div>
      )}
    </button>
  );
}

// Lista compacta de provas no histórico recente (encerradas). Cada linha
// mostra banco + data + acertos + duração; click abre o chooser do banco
// pra revisar/ver resultado.
function HistoricoList({ list }: { list: ProvaWithBank[] }) {
  return (
    <ul class="divide-y divide-border rounded-md border border-border bg-card/40">
      {list.map((p) => (
        <li key={`${p.bankId}-${p.id ?? p.encerradaEm}`}>
          <button
            type="button"
            onClick={() => {
              setActiveNav("bancos");
              openBank(p.bankId);
            }}
            class="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/30"
          >
            <div class="grid size-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
              <CheckCircle width="1.3em" height="1.3em" />
            </div>
            <div class="min-w-0 flex-1">
              <MarqueeOnHover className="text-sm font-semibold">
                {p.bankName}
              </MarqueeOnHover>
              <div class="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>
                  {p.encerradaEm
                    ? formatDateTime(new Date(p.encerradaEm).toISOString())
                    : "—"}
                </span>
                <span class="opacity-50">·</span>
                <span>
                  <span class="font-semibold text-foreground">{p.respondidas}</span>
                  /{p.total} respondidas
                </span>
              </div>
            </div>
            <div class="shrink-0 text-right">
              {p.percentualAcerto != null && (
                <div class="text-base font-bold text-primary">
                  {p.percentualAcerto}%
                </div>
              )}
              {p.duracaoMs > 0 && (
                <div class="text-[10px] text-muted-foreground">
                  {formatDuration(p.duracaoMs)}
                </div>
              )}
            </div>
            <div class="shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5">
              ›
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function formatDuration(ms: number): string {
  if (!ms || ms < 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ===== Modal base =====

function Modal({
  open,
  onClose,
  title,
  icon,
  children
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  icon: typeof HelpCircle;
  children: any;
}) {
  useBodyScrollLock(open);
  if (!open) return null;
  const Icon = icon;
  // Layout flex column: header fixo no topo, body cresce e rola dentro do
  // próprio modal. Backdrop NÃO rola (overflow-hidden) pra não competir
  // com o scroll interno. Modal é limitado a 85vh; o body usa flex-1 +
  // overflow-y-auto + .scroll-thin (scrollbar fino tematizado, mesmo do
  // legado).
  return (
    <div
      class="fixed inset-0 z-[60] grid min-h-full place-items-center overflow-hidden bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex shrink-0 items-center justify-between gap-3 border-b border-border px-6 py-4">
          <div class="flex items-center gap-2.5">
            <Icon width="1.3em" height="1.3em" className="text-primary" />
            <h2 class="text-lg font-bold">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="scroll-thin flex-1 overflow-y-auto px-6 py-6">{children}</div>
      </div>
    </div>
  );
}

// ===== Guia do Usuário =====

function GuiaSection({
  title,
  icon: Icon,
  children
}: {
  title: string;
  icon: typeof HelpCircle;
  children: any;
}) {
  return (
    <section class="mt-8 border-t border-border pt-7 first:mt-0 first:border-t-0 first:pt-0">
      <h3 class="mb-4 flex items-center gap-2 text-base font-bold">
        <Icon width="1.3em" height="1.3em" className="text-primary" />
        {title}
      </h3>
      <div class="space-y-3 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

function GuiaGrid({ children }: { children: any }) {
  return (
    <div
      class="my-3 grid gap-3"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
    >
      {children}
    </div>
  );
}

function GuiaCard({
  title,
  children
}: {
  title: string;
  children: any;
}) {
  return (
    <div class="rounded-md border border-border bg-accent/30 px-3 py-2.5">
      <div class="mb-1 text-xs font-bold text-foreground">{title}</div>
      <div class="text-xs text-muted-foreground">{children}</div>
    </div>
  );
}

function Kbd({ children }: { children: any }) {
  return (
    <kbd class="inline-block min-w-[1.5em] rounded border border-border bg-accent/50 px-1.5 py-0.5 text-center font-mono text-[10px] font-semibold text-foreground">
      {children}
    </kbd>
  );
}

function Shortcut({ keys, children }: { keys: any; children: any }) {
  return (
    <div class="flex items-start gap-2 text-xs">
      <div class="flex shrink-0 items-center gap-1">{keys}</div>
      <span class="text-muted-foreground">{children}</span>
    </div>
  );
}

function GuiaModal() {
  return (
    <Modal
      open={guiaOpen.value}
      onClose={() => (guiaOpen.value = false)}
      title="Guia do Usuário"
      icon={HelpCircle}
    >
      <p class="mb-6 text-sm text-muted-foreground">
        Este painel foi feito para ajudar você a estudar com mais foco,
        responder questões com mais organização e revisar o que realmente
        precisa de atenção.
      </p>

      <div class="space-y-6">
        <GuiaSection title="Como começar" icon={Rocket}>
          <p>
            Na tela inicial, escolha um banco para começar. Depois disso, você
            pode navegar pelas questões, acompanhar seu progresso e usar os
            recursos de revisão do próprio painel.
          </p>
          <GuiaGrid>
            <GuiaCard title="Tela inicial">
              Entre no banco que você quer estudar e continue de onde parou.
            </GuiaCard>
            <GuiaCard title="Menu lateral">
              Use para acessar bancos, provas, agenda, jogos e preferências.
            </GuiaCard>
            <GuiaCard title="Área de estudo">
              É onde você lê, responde e revisa as questões do banco.
            </GuiaCard>
          </GuiaGrid>
        </GuiaSection>

        <GuiaSection title="Responder questões" icon={CheckCircle}>
          <p>
            No estudo normal, você pode responder clicando na alternativa ou
            usando o teclado. Depois, fica mais fácil revisar o que acertou,
            errou ou ainda deixou pendente.
          </p>
          <p>
            Se quiser refazer uma questão, limpe a resposta atual e tente de
            novo. Também dá para filtrar a lista para focar só no que interessa
            naquele momento.
          </p>
        </GuiaSection>

        <GuiaSection title="Modo Prova" icon={ClipboardCheck}>
          <p>
            Use o <strong class="text-foreground">Modo Prova</strong> quando
            quiser treinar em formato mais próximo de avaliação. Você responde
            primeiro e revisa o resultado depois.
          </p>
          <p>
            Dá para acompanhar o tempo, pausar quando permitido e, no final,
            revisar seu desempenho com mais calma.
          </p>
        </GuiaSection>

        <GuiaSection title="Progresso e revisão" icon={BarChart3}>
          <p>
            O painel de progresso mostra quantas questões estão certas, erradas
            ou pendentes. Isso ajuda a organizar melhor a revisão.
          </p>
          <p>
            Quando quiser, filtre só acertos, erros ou pendentes para estudar
            com mais objetividade.
          </p>
        </GuiaSection>

        <GuiaSection title="Anotações" icon={StickyNote}>
          <p>
            Use as anotações para guardar lembretes, resumos curtos e pontos que
            você quer revisar depois.
          </p>
          <p>
            Você pode manter notas gerais do banco e também observações ligadas
            a questões específicas.
          </p>
        </GuiaSection>

        <GuiaSection title="Mapa, busca e filtros" icon={GridIcon}>
          <p>
            O mapa ajuda a pular rapidamente para qualquer questão do banco. A
            busca e os filtros ajudam a encontrar exatamente o que você quer
            revisar.
          </p>
          <p>
            Se uma questão tiver imagem, caso ou tabela, isso aparece na própria
            tela para apoiar sua leitura.
          </p>
        </GuiaSection>

        <GuiaSection title="Estudando com IA" icon={Sparkles}>
          <p>
            O <strong class="text-foreground">Modo Estudo com IA</strong> abre
            um chat por banco/tema com um assistente que explica questões,
            adapta o nível e responde dúvidas em texto livre.
          </p>
          <p>
            Configure sua chave gratuita do Google AI Studio em{" "}
            <strong class="text-foreground">Assistente IA</strong> (no menu) —
            o passo a passo está dentro de "Configure em 30s" e é grátis pra
            uso pessoal.
          </p>
          <p class="text-xs text-muted-foreground">
            Em <strong>Minha Conta &raquo; Perfil estendido</strong> você pode
            informar escola, curso e objetivos pra que as respostas sejam mais
            personalizadas pro seu contexto.
          </p>
        </GuiaSection>

        <GuiaSection title="Personalizando a sua home" icon={GridIcon}>
          <p>
            A página inicial tem blocos que você pode reordenar arrastando
            (botão "Personalizar" no canto superior direito da home). Dá pra
            esconder o que não usa e mudar a quantidade de colunas.
          </p>
          <p class="text-xs text-muted-foreground">
            A ordem fica salva por usuário e sincroniza entre dispositivos
            (via prefs no servidor).
          </p>
        </GuiaSection>

        <GuiaSection title="Suporte e FAQ" icon={HelpCircle}>
          <GuiaGrid>
            <GuiaCard title="Perguntas frequentes">
              Quando o admin habilita, aparece no menu a opção "Perguntas
              frequentes" com respostas para as dúvidas mais comuns.
            </GuiaCard>
            <GuiaCard title="Reportar bug">
              Em "Reportar algo" você abre um bug com descrição. A equipe
              responde direto na sua caixa — você é notificado.
            </GuiaCard>
            <GuiaCard title="Sugerir feature">
              No mesmo lugar, "Sugerir feature" envia ideia pra evolução.
              Toda contribuição ajuda.
            </GuiaCard>
          </GuiaGrid>
        </GuiaSection>

        <GuiaSection title="Recursos extras" icon={Music}>
          <GuiaGrid>
            <GuiaCard title="Jogos">
              Use os jogos como uma forma leve de variar o ritmo dos estudos.
            </GuiaCard>
            <GuiaCard title="Agenda">
              Acompanhe compromissos e momentos importantes da sua rotina.
            </GuiaCard>
            <GuiaCard title="Exportação e música">
              Quando fizer sentido, exporte conteúdo e use o player para manter
              o foco.
            </GuiaCard>
          </GuiaGrid>
        </GuiaSection>

        <GuiaSection title="Atalhos" icon={Keyboard}>
          <h4 class="text-xs font-bold uppercase tracking-wider text-foreground/80">
            Geral
          </h4>
          <div class="grid gap-1.5 sm:grid-cols-2">
            <Shortcut keys={<Kbd>H</Kbd>}>Abrir ou fechar este guia</Shortcut>
            <Shortcut keys={<Kbd>M</Kbd>}>Abrir ou fechar o mapa</Shortcut>
            <Shortcut keys={<Kbd>N</Kbd>}>Abrir ou fechar anotações</Shortcut>
            <Shortcut keys={<Kbd>Esc</Kbd>}>
              Fechar mapa, guia, modais ou o player
            </Shortcut>
            <Shortcut keys={<><Kbd>&larr;</Kbd><Kbd>&rarr;</Kbd></>}>
              Navegar entre questões
            </Shortcut>
            <Shortcut keys={<><Kbd>A</Kbd>-<Kbd>E</Kbd></>}>
              Responder por letra
            </Shortcut>
            <Shortcut keys={<><Kbd>1</Kbd>-<Kbd>5</Kbd></>}>
              Responder por número
            </Shortcut>
            <Shortcut keys={<Kbd>0</Kbd>}>Limpar resposta atual</Shortcut>
            <Shortcut keys={<><Kbd>Ctrl</Kbd>+<Kbd>F</Kbd></>}>
              Focar a busca
            </Shortcut>
            <Shortcut keys={<><Kbd>Ctrl</Kbd>+<Kbd>B</Kbd></>}>
              Alternar menu lateral
            </Shortcut>
            <Shortcut keys={<Kbd>T</Kbd>}>Alternar tema</Shortcut>
            <Shortcut
              keys={
                <>
                  <Kbd>Ctrl</Kbd>+<Kbd>/</Kbd> ou <Kbd>Ctrl</Kbd>+<Kbd>\</Kbd>
                </>
              }
            >
              Alternar tema
            </Shortcut>
          </div>

          <h4 class="mt-4 text-xs font-bold uppercase tracking-wider text-foreground/80">
            Player de música
          </h4>
          <div class="grid gap-1.5 sm:grid-cols-2">
            <Shortcut keys={<Kbd>S</Kbd>}>Abrir ou fechar o player</Shortcut>
            <Shortcut keys={<Kbd>Espaço</Kbd>}>Tocar ou pausar</Shortcut>
            <Shortcut keys={<><Kbd>N</Kbd> ou <Kbd>&rarr;</Kbd></>}>
              Próxima estação
            </Shortcut>
            <Shortcut keys={<><Kbd>B</Kbd> ou <Kbd>&larr;</Kbd></>}>
              Estação anterior
            </Shortcut>
            <Shortcut keys={<Kbd>L</Kbd>}>Abrir ou fechar a lista</Shortcut>
            <Shortcut keys={<><Kbd>↑</Kbd><Kbd>↓</Kbd><Kbd>Enter</Kbd></>}>
              Navegar e escolher estação na lista
            </Shortcut>
            <Shortcut keys={<><Kbd>+</Kbd> / <Kbd>=</Kbd></>}>
              Aumentar volume
            </Shortcut>
            <Shortcut keys={<><Kbd>-</Kbd> / <Kbd>_</Kbd></>}>
              Diminuir volume
            </Shortcut>
            <Shortcut keys={<><Kbd>M</Kbd> ou <Kbd>0</Kbd></>}>
              Ativar ou desativar mudo
            </Shortcut>
          </div>
        </GuiaSection>

        <div class="mt-6 border-t border-border pt-4">
          <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Documentos legais
          </p>
          <LegalLinks />
        </div>
      </div>
    </Modal>
  );
}
// ===== Preferências =====

function PrefRow({
  title,
  description,
  badge,
  hint,
  control
}: {
  title: string;
  description?: string;
  badge?: string;
  hint?: string;
  control: any;
}) {
  return (
    <div class="border-t border-border py-5 first:border-t-0 first:pt-0">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <h4 class="text-sm font-semibold">{title}</h4>
            {badge && (
              <Badge variant="outline" className="text-[10px]">
                {badge}
              </Badge>
            )}
          </div>
          {description && (
            <p class="mt-1 text-xs text-muted-foreground">{description}</p>
          )}
          {hint && (
            <p class="mt-1 text-[11px] italic text-muted-foreground/70">{hint}</p>
          )}
        </div>
        <div class="shrink-0">{control}</div>
      </div>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  disabled
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Switch
      checked={checked}
      onCheckedChange={(next) => onChange(next === true)}
      disabled={disabled}
    />
  );
}

// Segmented control de 4 modos pras "janelinhas" no canto da tela:
// unifica `mensagensDock` (inbox no canto inferior esquerdo, lista de
// não-lidas) + `peerChatAutoOpen` (conversa que abre sozinha no canto
// inferior direito quando chega chat 1-a-1). Antes eram dois toggles
// soltos que pareciam redundantes — agora viraram uma escolha única.
type JanelinhasMode = "off" | "inbox" | "auto" | "both";

function janelinhasModeFromPrefs(
  inbox: boolean,
  autoOpen: boolean
): JanelinhasMode {
  if (inbox && autoOpen) return "both";
  if (inbox) return "inbox";
  if (autoOpen) return "auto";
  return "off";
}

function JanelinhasModePicker({
  inbox,
  autoOpen,
  onChange
}: {
  inbox: boolean;
  autoOpen: boolean;
  onChange: (nextInbox: boolean, nextAutoOpen: boolean) => void;
}) {
  const current = janelinhasModeFromPrefs(inbox, autoOpen);
  const opts: Array<{
    key: JanelinhasMode;
    label: string;
    title: string;
    Icon: typeof Ban;
  }> = [
    { key: "off", label: "Desligado", title: "Nenhuma janelinha flutuante", Icon: Ban },
    {
      key: "inbox",
      label: "Inbox",
      title: "Lista de mensagens não-lidas no canto inferior esquerdo",
      Icon: Inbox
    },
    {
      key: "auto",
      label: "Auto-abrir",
      title:
        "Quando alguém te manda chat, a conversa abre sozinha no canto inferior direito",
      Icon: PictureInPicture2
    },
    {
      key: "both",
      label: "Ambos",
      title: "Inbox à esquerda + auto-abrir conversa à direita",
      Icon: MessageCircle
    }
  ];
  return (
    <div class="inline-flex shrink-0 overflow-hidden rounded-lg border border-border bg-accent/30">
      {opts.map((o) => {
        const active = current === o.key;
        const next: [boolean, boolean] =
          o.key === "off"
            ? [false, false]
            : o.key === "inbox"
              ? [true, false]
              : o.key === "auto"
                ? [false, true]
                : [true, true];
        const Icon = o.Icon;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(next[0], next[1])}
            class={`inline-flex h-10 items-center gap-1.5 px-3 text-xs font-semibold transition-colors ${
              active
                ? "bg-primary text-white"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
            title={o.title}
            aria-label={o.title}
          >
            <Icon className="size-3.5" />
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Mini-picker das 6 variantes de animação — cada uma com toggle e botão
// de preview. Aparece logo abaixo do toggle master de "Comemoração" quando
// ele está ligado, pra dar visibilidade dos estilos sem precisar de modal
// dentro de modal.
const VARIANT_LABELS: Record<AnimationVariant, { label: string; emoji: string }> = {
  confete: { label: "Confete", emoji: "🎊" },
  fogos: { label: "Fogos", emoji: "🎆" },
  estrelas: { label: "Estrelas", emoji: "⭐" },
  emojis: { label: "Emojis", emoji: "🎉" },
  baloes: { label: "Balões", emoji: "🎈" },
  champanhe: { label: "Champanhe", emoji: "🥂" }
};

function AnimationVariantPicker({ prefs: p }: { prefs: PrefsState }) {
  return (
    <div class="ml-4 mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
      {ANIMATION_VARIANTS.map((variant) => {
        const v = VARIANT_LABELS[variant];
        const checked = p.comemoracaoStyles[variant];
        return (
          <div
            key={variant}
            class={`flex items-center gap-2 rounded-md border px-2.5 py-2 transition-colors ${
              checked
                ? "border-primary/40 bg-primary/5"
                : "border-border bg-card/40"
            }`}
          >
            <button
              type="button"
              onClick={() =>
                savePrefs({
                  ...p,
                  comemoracaoStyles: {
                    ...p.comemoracaoStyles,
                    [variant]: !checked
                  }
                })
              }
              class="flex flex-1 items-center gap-2 text-left"
              aria-pressed={checked}
            >
              <span class="text-lg leading-none">{v.emoji}</span>
              <span class="text-sm font-medium">{v.label}</span>
            </button>
            <button
              type="button"
              onClick={() => previewAnimationVariant(variant)}
              title="Tocar prévia"
              aria-label={`Prévia de ${v.label}`}
              class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <PlayCircle width="1.3em" height="1.3em" />
            </button>
            <Toggle
              checked={checked}
              onChange={(nv) =>
                savePrefs({
                  ...p,
                  comemoracaoStyles: {
                    ...p.comemoracaoStyles,
                    [variant]: nv
                  }
                })
              }
            />
          </div>
        );
      })}
    </div>
  );
}

function VibrationTypesPicker({ prefs: p }: { prefs: PrefsState }) {
  const types: Array<{
    key: keyof PrefsState["vibracaoTipos"];
    label: string;
    desc: string;
  }> = [
    { key: "correct", label: "Ao acertar", desc: "Pulso curto (60 ms)" },
    { key: "wrong", label: "Ao errar", desc: "Padrão de shake" },
    { key: "tap", label: "Toques em botão", desc: "Tap leve (15 ms)" }
  ];
  return (
    <div class="ml-4 mt-2 space-y-1.5">
      {types.map((t) => (
        <div
          key={t.key}
          class="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-3 py-2"
        >
          <div class="min-w-0">
            <div class="text-sm font-medium">{t.label}</div>
            <div class="text-xs text-muted-foreground">{t.desc}</div>
          </div>
          <Toggle
            checked={p.vibracaoTipos[t.key]}
            onChange={(v) =>
              savePrefs({
                ...p,
                vibracaoTipos: { ...p.vibracaoTipos, [t.key]: v }
              })
            }
          />
        </div>
      ))}
    </div>
  );
}

function PreferencesModal() {
  const isDark = themeSignal.value === "dark";
  const p = prefs.value;
  const [biometriaBusy, setBiometriaBusy] = useState(false);
  const [biometriaMsg, setBiometriaMsg] = useState<string | null>(null);
  const [biometriaConfirmOff, setBiometriaConfirmOff] = useState(false);
  // Verdade da fonte: existe passkey deste dispositivo no servidor?
  // O state de prefs (localStorage) pode estar desincronizado se outro
  // dispositivo cadastrou/removeu — então toda abertura do modal verifica.
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);

  // Ao abrir o modal de preferências, consulta o servidor pra saber se
  // este dispositivo já tem passkey. Sincroniza o toggle com a realidade.
  useEffect(() => {
    if (!prefsOpen.value) return;
    let cancelled = false;
    setHasPasskey(null);
    countCurrentDevicePasskeys().then((n) => {
      if (cancelled) return;
      const exists = n > 0;
      setHasPasskey(exists);
      // Reconcilia o pref local com o servidor
      if (exists !== prefs.value.biometria) {
        savePrefs({ ...prefs.value, biometria: exists });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [prefsOpen.value]);

  async function doEnableBiometria() {
    setBiometriaMsg(null);
    setBiometriaBusy(true);
    try {
      await registerCurrentDevicePasskey();
      savePrefs({ ...prefs.value, biometria: true });
      setHasPasskey(true);
      setBiometriaMsg("Biometria ativada neste dispositivo.");
    } catch (err: any) {
      const msg = err?.message || String(err);
      if (/cancelled|aborted|notallowed/i.test(msg)) {
        setBiometriaMsg("Cadastro de biometria cancelado.");
      } else {
        setBiometriaMsg(msg);
      }
    } finally {
      setBiometriaBusy(false);
    }
  }

  async function doDisableBiometria() {
    setBiometriaMsg(null);
    setBiometriaBusy(true);
    try {
      const removed = await removeCurrentDevicePasskeys();
      savePrefs({ ...prefs.value, biometria: false });
      setHasPasskey(false);
      setBiometriaMsg(
        removed > 0
          ? "Biometria removida deste dispositivo."
          : "Nenhuma credencial local foi encontrada; a preferência foi desligada."
      );
    } catch (err: any) {
      setBiometriaMsg(err?.message || String(err));
    } finally {
      setBiometriaBusy(false);
      setBiometriaConfirmOff(false);
    }
  }

  function handleBiometriaToggle(next: boolean) {
    setBiometriaMsg(null);
    if (next) {
      if (!supportsBiometria()) {
        setBiometriaMsg("Seu navegador não suporta biometria WebAuthn neste contexto.");
        return;
      }
      void doEnableBiometria();
    } else {
      // Desligar = remover dispositivo da lista de biometria. Confirma
      // antes pra evitar perder o vínculo por engano.
      setBiometriaConfirmOff(true);
    }
  }

  return (
    <Modal
      open={prefsOpen.value}
      onClose={() => (prefsOpen.value = false)}
      title="Preferências"
      icon={Settings}
    >
      <PrefRow
        title="Aparência"
        description="Tema claro ou escuro. Acompanha o sistema na primeira abertura."
        control={
          <div class="inline-flex overflow-hidden rounded-lg border border-border bg-accent/30">
            <button
              type="button"
              onClick={() => isDark && toggleTheme()}
              class={`inline-flex h-10 items-center gap-2 px-4 text-sm font-semibold transition-colors ${
                !isDark
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
              title="Tema claro"
              aria-label="Ativar tema claro"
              aria-pressed={!isDark ? "true" : "false"}
            >
              <Sun className="size-4" />
              <span>Claro</span>
            </button>
            <button
              type="button"
              onClick={() => !isDark && toggleTheme()}
              class={`inline-flex h-10 items-center gap-2 px-4 text-sm font-semibold transition-colors ${
                isDark
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
              title="Tema escuro"
              aria-label="Ativar tema escuro"
              aria-pressed={isDark ? "true" : "false"}
            >
              <Moon className="size-4" />
              <span>Escuro</span>
            </button>
          </div>
        }
      />

      <PrefRow
        title="Comemoração ao acertar"
        description="Festa visual quando você responde corretamente no Modo Estudo. Seis estilos alternam aleatoriamente."
        hint="Aparece só no Modo Estudo. Em provas o foco é avaliação — sem distração."
        control={
          <Toggle
            checked={p.comemoracao}
            onChange={(v) => savePrefs({ ...p, comemoracao: v })}
          />
        }
      />

      {p.comemoracao && <AnimationVariantPicker prefs={p} />}

      {/* Sub-celebrações por tipo de correção. Gated pelo master "Comemoração
          ao acertar" — quando master = off, nada celebra. Esses toggles
          escolhem ALÉM da objetiva (que celebra por questão) se também queremos
          celebrar dissertativa por bloco e redação ao receber nota. Persistem
          em users.prefs.aiCorrection (server-synced, não localStorage). */}
      {p.comemoracao && (
        <>
          <PrefRow
            title="Comemorar dissertativa por bloco"
            description="Festa visual ao fechar um bloco se a média das dissertativas ficar maior ou igual à nota mínima (configurável em Minha Conta → Preferências da IA)."
            hint="Vale apenas em Modo Estudo e Simulação. Prova Agendada não dispara animação."
            control={
              <Toggle
                checked={aiCorrectionPrefs.value.celebrateDissertativeBlock}
                onChange={(v) => {
                  void setCelebrateDissertativeBlock(v);
                }}
              />
            }
          />

          <PrefRow
            title="Comemorar redação corrigida"
            description="Festa visual quando a IA devolve uma nota maior ou igual à nota mínima na sua redação (sessão de Estudando com IA)."
            hint="Dispara uma vez por correção. Regerar a correção pode disparar de novo se a nota subir."
            control={
              <Toggle
                checked={aiCorrectionPrefs.value.celebrateEssay}
                onChange={(v) => {
                  void setCelebrateEssay(v);
                }}
              />
            }
          />
        </>
      )}

      <PrefRow
        title="Vibração"
        description="Feedback tátil ao responder. Padrões diferentes para acerto e erro."
        hint="Funciona em Android (suporte do navegador). iOS Safari não suporta — toggles ficam sem efeito mesmo ligados."
        control={
          <Toggle
            checked={p.vibracao}
            onChange={(v) => savePrefs({ ...p, vibracao: v })}
          />
        }
      />

      {p.vibracao && <VibrationTypesPicker prefs={p} />}

      <PrefRow
        title="Deslizar para alternar questões"
        description="Arraste o conteúdo para a esquerda ou direita para passar de uma questão à outra."
        hint="Disponível apenas em dispositivos com tela sensível ao toque."
        control={
          <Toggle
            checked={p.swipe}
            onChange={(v) => savePrefs({ ...p, swipe: v })}
          />
        }
      />

      <PrefRow
        title="Player de música para foco"
        description="Botão flutuante no canto inferior direito que abre o player de rádios para concentração."
        hint="Atalho S abre/fecha o player rapidamente. A última estação escolhida é lembrada."
        control={
          <Toggle
            checked={p.musicPlayer}
            onChange={(v) => savePrefs({ ...p, musicPlayer: v })}
          />
        }
      />

      <PrefRow
        title="Janelinhas de mensagens no canto da tela"
        description="UIs flutuantes para mensagens — escolha como elas aparecem. Funciona apenas em telas largas."
        hint="No celular você continua usando a barra lateral. As janelinhas seguem o estilo LinkedIn."
        control={
          <JanelinhasModePicker
            inbox={p.mensagensDock}
            autoOpen={p.peerChatAutoOpen}
            onChange={(inbox, autoOpen) =>
              savePrefs({ ...p, mensagensDock: inbox, peerChatAutoOpen: autoOpen })
            }
          />
        }
      />

      <PrefRow
        title="Biometria neste dispositivo"
        badge={hasPasskey ? "Ativa" : undefined}
        description="Use a câmera, digital, PIN ou chave do dispositivo para entrar sem senha quando este email estiver lembrado."
        hint="Ao ligar, o navegador pedirá para gravar uma passkey. Ao desligar, removemos o vínculo deste dispositivo para este usuário."
        control={
          <Toggle
            checked={p.biometria}
            onChange={handleBiometriaToggle}
            disabled={biometriaBusy || hasPasskey === null}
          />
        }
      />

      {biometriaMsg && (
        <div class="ml-4 rounded-md border border-border bg-accent/20 px-3 py-2 text-xs text-muted-foreground">
          {biometriaMsg}
        </div>
      )}

      {biometriaConfirmOff && (
        <ConfirmRemoveBiometriaDialog
          busy={biometriaBusy}
          onCancel={() => setBiometriaConfirmOff(false)}
          onConfirm={doDisableBiometria}
        />
      )}
    </Modal>
  );
}

function ConfirmRemoveBiometriaDialog({
  busy,
  onCancel,
  onConfirm
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      class="fixed inset-0 z-[120] grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onCancel();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div class="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-2xl">
        <h3 class="text-base font-bold">Remover biometria deste dispositivo?</h3>
        <p class="mt-2 text-sm text-muted-foreground">
          A passkey gravada neste dispositivo será apagada. Você ainda pode
          entrar com e-mail e senha — e cadastrar a biometria de novo a
          qualquer momento. Outros dispositivos com biometria continuam ativos.
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            class="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm font-bold text-destructive hover:bg-destructive/20 disabled:opacity-50"
          >
            {busy && <Loader2 className="size-3.5 animate-spin" />}
            Remover biometria
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Bancos de questões =====

const MES_ABBR = [
  "JAN",
  "FEV",
  "MAR",
  "ABR",
  "MAI",
  "JUN",
  "JUL",
  "AGO",
  "SET",
  "OUT",
  "NOV",
  "DEZ"
];

function formatDateTimeAbsolute(iso: string): string {
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = MES_ABBR[d.getMonth()];
  const ano = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dia} ${mes} ${ano} ${hh}:${mm}`;
}

function formatLastActivityRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const dia = 24 * 60 * 60 * 1000;
  if (diffMs < dia) return "hoje";
  if (diffMs < 2 * dia) return "ontem";
  if (diffMs < 7 * dia) return `há ${Math.round(diffMs / dia)} dias`;
  if (diffMs < 30 * dia) return `há ${Math.round(diffMs / (7 * dia))} sem.`;
  if (diffMs < 365 * dia) return `há ${Math.round(diffMs / (30 * dia))} meses`;
  return `há ${Math.round(diffMs / (365 * dia))} anos`;
}

function isRecent(iso: string | null): boolean {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 7 * 24 * 60 * 60 * 1000;
}

// MarqueeOnHover está em ./Marquee.tsx (compartilhado com BankChooserModal etc)

function DoubleProgressBar({
  respondidasPct,
  acertosPct
}: {
  respondidasPct: number;
  acertosPct: number;
}) {
  // Camada base (track): cinza/dark muted
  // Camada faded: % respondidas (violeta chapado opaco/muted)
  // Camada forte: % acertos (violeta chapado, sobreposto) — sem degradê.
  const respW = Math.max(0, Math.min(100, respondidasPct));
  const accW = Math.max(0, Math.min(100, acertosPct));
  return (
    <div class="relative mt-2 h-2.5 overflow-hidden rounded-full bg-border/60">
      {/* Faded — total respondidas */}
      <div
        class="absolute inset-y-0 left-0 bg-primary/35 transition-all"
        style={{ width: `${respW}%` }}
      />
      {/* Forte — acertos (sempre desenhado sobre o faded) */}
      <div
        class="absolute inset-y-0 left-0 bg-primary transition-all"
        style={{ width: `${accW}%` }}
      />
    </div>
  );
}

export function BankCard({
  bank,
  compact = false
}: {
  bank: BankRow;
  compact?: boolean;
}) {
  const novo = !bank.lastActivityAt;
  const pct = bank.estudo.accuracyPct;
  const progressPct =
    bank.questionCount > 0
      ? Math.round((bank.estudo.respondidas / bank.questionCount) * 100)
      : 0;
  const acertosPct =
    bank.questionCount > 0
      ? Math.round((bank.estudo.acertos / bank.questionCount) * 100)
      : 0;
  const lastAbsolute = bank.lastActivityAt
    ? formatDateTimeAbsolute(bank.lastActivityAt)
    : null;

  // Texto exibido no bloco PROVA conforme estado
  let provaText: any = null;
  if (bank.provas.total > 0 || bank.provas.emAndamento) {
    if (bank.provas.emAndamento) {
      provaText = (
        <>
          Toque para retomar
          {bank.provas.total > 0 && (
            <>
              <span class="mx-1.5 opacity-60">·</span>
              {bank.provas.total} tentativa
              {bank.provas.total === 1 ? "" : "s"}
            </>
          )}
          {bank.provas.melhorPercentual != null && (
            <>
              <span class="mx-1.5 opacity-60">·</span>
              melhor {bank.provas.melhorPercentual}%
            </>
          )}
        </>
      );
    } else {
      provaText = (
        <>
          {bank.provas.total} tentativa{bank.provas.total === 1 ? "" : "s"}
          {bank.provas.melhorPercentual != null && (
            <>
              <span class="mx-1.5 opacity-60">·</span>
              melhor {bank.provas.melhorPercentual}%
            </>
          )}
        </>
      );
    }
  }

  function handleOpen() {
    setActiveNav("bancos");
    openBank(bank.id);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      }}
      class="group relative flex h-full w-full cursor-pointer flex-col rounded-md border border-border bg-card text-left transition-all hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 focus:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary"
    >
      {/* Badge na borda — "Em andamento" tem prioridade visual; senão "Novo" */}
      {bank.provas.emAndamento ? (
        <span
          class="absolute right-4 top-0 z-10 inline-flex -translate-y-1/2 items-center gap-1 rounded-md bg-amber-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-md shadow-amber-500/30 ring-1 ring-amber-300/60"
          title="Prova em andamento — toque para retomar"
        >
          <span class="relative flex size-1.5">
            <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-90" />
            <span class="relative inline-flex size-1.5 rounded-full bg-white" />
          </span>
          <PlayCircle className="size-3" strokeWidth={2.8} />
          Em andamento
        </span>
      ) : novo ? (
        <span class="absolute right-4 top-0 z-10 -translate-y-1/2 rounded-md bg-emerald-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-sm">
          Novo
        </span>
      ) : null}

      {/* Header: file icon + título marquee + menu de pasta */}
      <div class="flex items-center gap-3 px-5 pt-5">
        <div class="grid size-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
          <FileText className="size-4" />
        </div>
        <div class="min-w-0 flex-1">
          <MarqueeOnHover className="text-base font-bold leading-tight">
            {bank.name}
          </MarqueeOnHover>
          <div class="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span>
              {bank.questionCount}{" "}
              {bank.questionCount === 1 ? "questão" : "questões"}
            </span>
            <BankKindBadges bank={bank} />
            {bank.sharedByName && (
              <span
                class="inline-flex items-center gap-0.5 rounded-md border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-300"
                title={`Compartilhado por ${bank.sharedByName}`}
              >
                <Share2 className="size-2.5" />
                por {bank.sharedByName.split(" ")[0]}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Blocos */}
      <div class="space-y-2 px-5 py-4">
        {/* ESTUDO — tom azul (não violeta) pra diferenciar de Prova. Match
            com o card do Simulador legado, onde Estudo era azul-noite e
            Prova era âmbar. Bordas explícitas como nos ActionRow do detalhe. */}
        <div class="rounded-md border border-blue-500/40 bg-blue-500/5 px-3 py-2.5 transition-colors group-hover:border-blue-500/60 group-hover:bg-blue-500/10">
          <div class="flex items-start gap-2.5">
            <div class="grid size-8 shrink-0 place-items-center rounded-md bg-blue-500/20 text-blue-500 ring-1 ring-blue-500/30 dark:text-blue-400">
              <BookOpen className="size-4" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Estudo
              </div>
              <MarqueeOnHover className="text-xs">
                <span class="font-bold text-foreground">
                  {bank.estudo.respondidas} de {bank.questionCount}
                </span>
                <span class="ml-1 text-muted-foreground">respondidas</span>
                {pct != null && (
                  <>
                    <span class="mx-1.5 opacity-50">·</span>
                    <span class="font-bold text-foreground">{pct}%</span>
                    <span class="ml-1 text-muted-foreground">de acerto</span>
                  </>
                )}
              </MarqueeOnHover>
              <DoubleProgressBar
                respondidasPct={progressPct}
                acertosPct={acertosPct}
              />
            </div>
          </div>
        </div>

        {/* PROVA — bloco âmbar se em andamento. Bordas explícitas em ambos
            os estados pra ficar consistente com o ESTUDO acima e os ActionRow
            do detalhe. */}
        {provaText && (
          <div
            class={`rounded-md border px-3 py-2.5 transition-colors ${
              bank.provas.emAndamento
                ? "border-amber-500/40 bg-amber-500/5 group-hover:border-amber-500/60 group-hover:bg-amber-500/10"
                : "border-border bg-accent/20 group-hover:border-primary/50 group-hover:bg-accent/40"
            }`}
          >
            <div class="flex items-start gap-2.5">
              <div
                class={`grid size-8 shrink-0 place-items-center rounded-md ring-1 ${
                  bank.provas.emAndamento
                    ? "bg-amber-500/25 text-amber-600 ring-amber-500/40 dark:text-amber-400"
                    : "bg-primary/15 text-primary ring-primary/20"
                }`}
              >
                <ClipboardCheck className="size-4" />
              </div>
              <div class="min-w-0 flex-1">
                <div
                  class={`text-[10px] font-bold uppercase tracking-wider ${
                    bank.provas.emAndamento
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-muted-foreground"
                  }`}
                >
                  Prova
                </div>
                <MarqueeOnHover className="text-xs text-muted-foreground">
                  {provaText}
                </MarqueeOnHover>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div class="mt-auto border-t border-border/60 px-5 py-2.5">
        <MarqueeOnHover className="text-[11px] text-muted-foreground">
          {lastAbsolute ? (
            <>Visto por último {lastAbsolute}</>
          ) : (
            "Nunca aberto"
          )}
        </MarqueeOnHover>
      </div>
    </div>
  );
}

/** Modo lista (view-mode "list") — uma linha por banco, densa.
 *  Mostra ícone, nome, contagens (questões, respondidas, acerto, melhor de prova)
 *  e ações: abrir + mover. Padrão Windows Explorer / Finder. */
function BankRowLine({
  bank,
  isLast
}: {
  bank: BankRow;
  isLast: boolean;
}) {
  const pct = bank.estudo.accuracyPct;
  return (
    <div
      class={`flex items-center gap-3 px-4 py-2 transition-colors hover:bg-accent/40 ${
        isLast ? "" : "border-b border-border/60"
      }`}
    >
      <div class="grid size-8 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
        <FileText className="size-3.5" />
      </div>
      <button
        type="button"
        onClick={() => {
          setActiveNav("bancos");
          openBank(bank.id);
        }}
        class="min-w-0 flex-1 text-left focus:outline-none"
      >
        <div class="truncate text-sm font-semibold">{bank.name}</div>
        <div class="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
          <span>{bank.questionCount} questões</span>
          <span class="opacity-50">·</span>
          <span>
            {bank.estudo.respondidas} respondidas
            {pct != null && ` · ${pct}% acerto`}
          </span>
          {bank.provas.emAndamento && (
            <>
              <span class="opacity-50">·</span>
              <span class="font-semibold text-amber-600 dark:text-amber-400">
                Prova em andamento
              </span>
            </>
          )}
          {bank.sharedByName && (
            <>
              <span class="opacity-50">·</span>
              <span class="text-blue-600 dark:text-blue-300">
                por {bank.sharedByName.split(" ")[0]}
              </span>
            </>
          )}
        </div>
      </button>
    </div>
  );
}

// Skeleton do Meu Acervo — replica a estrutura real (tree aside + toolbar
// + folder tiles + bank cards) em vez de mostrar só 9 cards genéricos. Quando
// banksPhase=="loading" o usuário vê um esqueleto fiel do layout, evitando
// CLS (content layout shift) quando os dados chegam.
function MeuAcervoSkeleton() {
  return (
    <div class="flex flex-col gap-4 lg:flex-row">
      {/* Tree aside */}
      <aside class="hidden w-full shrink-0 rounded-md border border-border bg-card/60 p-2 lg:block lg:w-72">
        <Skeleton className="mb-2 ml-2 h-2.5 w-12" />
        <div class="space-y-1">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} class="flex items-center gap-2 px-2 py-1">
              <Skeleton className="size-3.5 rounded" />
              <Skeleton
                className={`h-3 ${
                  i % 3 === 0 ? "w-2/3" : i % 2 === 0 ? "w-3/4" : "w-1/2"
                }`}
              />
              <Skeleton className="ml-auto h-2.5 w-4" />
            </div>
          ))}
        </div>
      </aside>
      {/* Content */}
      <section class="min-w-0 flex-1 space-y-4">
        {/* Topbar: busca + 2 botões + (desfazer/refazer + view-mode) */}
        <div class="space-y-2">
          <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Skeleton className="h-9 w-full sm:flex-1" />
            <div class="flex gap-1.5">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-36" />
            </div>
          </div>
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex gap-1">
              <Skeleton className="h-7 w-20 rounded-md" />
              <Skeleton className="h-7 w-20 rounded-md" />
            </div>
            <Skeleton className="h-7 w-24 rounded-md" />
          </div>
        </div>
        {/* ContentHeader: breadcrumb + título + contadores */}
        <div class="border-b border-border/60 pb-2.5">
          <Skeleton className="mb-2 h-2.5 w-24" />
          <Skeleton className="h-5 w-48" />
        </div>
        {/* Pastas tiles (3 tiles) */}
        <div>
          <Skeleton className="mb-1.5 h-2.5 w-16" />
          <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                class="flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2"
              >
                <Skeleton className="size-4 rounded" />
                <div class="min-w-0 flex-1 space-y-1">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-2.5 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Grid de bancos (6 cards) */}
        <div>
          <Skeleton className="mb-1.5 h-2.5 w-16" />
          <div class="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <BankCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function BankCardSkeleton() {
  return (
    <Card className="border-border/70 bg-card/70">
      <CardHeader className="pb-3">
        <div class="flex items-start gap-3">
          <Skeleton className="size-10 shrink-0 rounded-md" />
          <div class="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div class="rounded-md border border-border/50 bg-accent/20 px-3 py-2.5">
          <div class="flex items-center justify-between">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="mt-2 h-1.5 w-full" />
        </div>
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-9 w-full" />
      </CardContent>
    </Card>
  );
}

function BanksView() {
  if (banksPhase.value === "loading") {
    // Skeleton espelha o layout NOVO do Meu Acervo (tree à esquerda + topbar
    // + grid de cards) em vez do grid genérico antigo. Pedido 2026-05-20.
    return <MeuAcervoSkeleton />;
  }

  if (banksPhase.value === "error") {
    return (
      <Alert variant="destructive">
        <AlertCircle width="1.3em" height="1.3em" />
        <AlertTitle>Erro ao carregar bancos</AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p>{banksError.value}</p>
          <Button size="sm" onClick={() => loadBanks()}>
            Tentar de novo
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Se entrou em um banco específico (subCrumb ativo), mostra placeholder de estudo
  if (subCrumbs.value.length >= 2) {
    const bankCrumb = subCrumbs.value[0];
    return (
      <div class="space-y-4">
        <Card className="border-dashed border-primary/40 bg-primary/5">
          <CardContent className="space-y-3 p-8 text-center">
            <div class="mx-auto grid size-14 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/30">
              <PlayCircle className="size-7" />
            </div>
            <div>
              <h3 class="text-lg font-bold">{bankCrumb.label}</h3>
              <p class="mt-1 text-sm text-muted-foreground">
                Modo estudo em construção. Em breve a tela de praticar questões
                virá aqui.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => (subCrumbs.value = [])}
            >
              Voltar pros bancos
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allBanks = banksData.value;
  if (allBanks.length === 0) {
    return (
      <div class="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <div class="grid size-16 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
          <Database className="size-7" />
        </div>
        <h2 class="mt-4 text-lg font-bold">Nenhum banco disponível</h2>
        <p class="mt-1 max-w-md text-sm text-muted-foreground">
          Quando seu professor publicar conteúdo nas suas disciplinas, ou
          quando houver bancos públicos da sua Organização, eles aparecem aqui.
        </p>
      </div>
    );
  }

  // File Explorer estilo Windows: tree de pastas (esquerda) + grid de bancos
  // (direita), com subpastas, drag-and-drop cross-pane, undo/redo (Ctrl+Z/Y),
  // tiles de pastas em modo raiz, view-mode cards/compacto/lista. Toda a
  // lógica vive em [[MeuAcervoExplorer]].
  return <MeuAcervoExplorer />;
}

function ComingSoonView({ title, icon: Icon }: { title: string; icon: typeof Home }) {
  return (
    <div class="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div class="grid size-16 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
        <Icon className="size-7" />
      </div>
      <h1 class="mt-4 text-2xl font-bold">{title}</h1>
      <p class="mt-1 max-w-md text-sm text-muted-foreground">
        Em breve. Estamos focando primeiro em deixar a tela inicial completa.
      </p>
    </div>
  );
}

// Página dedicada do Assistente IA — config de provedor, BYOK e on/off.
// Acessível pelo item "Assistente IA" no menu lateral. O painel inline
// dentro do AiAssistant.tsx (Modo Estudo) é a versão compacta; aqui
// dá pra explicar com mais espaço.
function AiSettingsView({ embedded = false }: { embedded?: boolean } = {}) {
  const prefs = uiPrefs.value;
  const { providerCatalog, strategy } = useProviderCatalog(prefs.aiProvider);
  const [connections, setConnections] = useState<AiConnectionsResponse | null>(null);
  const [googleConnectNotice, setGoogleConnectNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .aiConnections()
      .then((resp) => {
        if (!cancelled) {
          setConnections(resp);
          const hasConnectedGemini = resp.connections.some(
            (item) => item.provider === "gemini" && item.status === "connected"
          );
          if (uiPrefs.value.aiAccessMode === "connected" && !hasConnectedGemini) {
            setAiAccessMode("platform");
          }
        }
      })
      .catch(() => {
        if (!cancelled) setConnections(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const status = url.searchParams.get("ai_google");
    const reason = url.searchParams.get("reason");
    if (status === "connected") {
      setGoogleConnectNotice("Conta Google conectada com sucesso para usar Gemini.");
    } else if (status === "error") {
      setGoogleConnectNotice(
        reason
          ? `Nao foi possivel conectar a conta Google: ${reason}`
          : "Nao foi possivel conectar a conta Google."
      );
    } else {
      return;
    }
    url.searchParams.delete("ai_google");
    url.searchParams.delete("reason");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  return (
    <div class="space-y-5">
      {!embedded && (
        <header>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Sparkles className="size-6 text-primary" />
            Assistente IA
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Tutor por questão no Modo Estudo. Detecta o tema da questão e adota
            uma persona de professor para te ajudar a entender com a profundidade
            certa.
          </p>
        </header>
      )}

      <section class="rounded-md border border-border bg-card/60 p-5">
        <h2 class="text-base font-bold">Habilitado</h2>
        <p class="mt-1 text-sm text-muted-foreground">
          Quando ligado, um botão "IA" aparece no header do Modo Estudo e abre
          uma sidebar de chat ao lado da lista de questões.
        </p>
        <label class="mt-3 inline-flex items-center gap-2 text-sm">
          <Switch
            checked={prefs.aiAssistantEnabled}
            onCheckedChange={(v) => setAiAssistantEnabled(v)}
          />
          <span>Habilitar Assistente IA no Modo Estudo</span>
        </label>
      </section>

      <section class="rounded-md border border-border bg-card/60 p-5">
        <h2 class="text-base font-bold">Provedor (BYOK opcional)</h2>
        <p class="mt-1 text-sm text-muted-foreground">
          Por padrão usamos uma chave compartilhada com limites mais
          conservadores. Se quiser mais liberdade, você pode usar sua própria
          chave e escolher entre Gemini, GPT / Codex, Claude ou Grok.
        </p>
        <div class="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAiAccessMode("platform")}
            aria-pressed={prefs.aiAccessMode === "platform" ? "true" : "false"}
            class={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              prefs.aiAccessMode === "platform"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background hover:bg-accent"
            }`}
          >
            Plataforma
          </button>
          <button
            type="button"
            onClick={() => setAiAccessMode("byok")}
            aria-pressed={prefs.aiAccessMode === "byok" ? "true" : "false"}
            class={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              prefs.aiAccessMode === "byok"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background hover:bg-accent"
            }`}
          >
            Minha API key
          </button>
          {prefs.aiProvider === "gemini" && (
            <button
              type="button"
              onClick={() => setAiAccessMode("connected")}
              disabled={
                !connections?.connections.some(
                  (item) => item.provider === "gemini" && item.status === "connected"
                )
              }
              aria-disabled={
                !connections?.connections.some(
                  (item) => item.provider === "gemini" && item.status === "connected"
                )
                  ? "true"
                  : "false"
              }
              class={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                prefs.aiAccessMode === "connected"
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background"
              } ${
                !connections?.connections.some(
                  (item) => item.provider === "gemini" && item.status === "connected"
                )
                  ? "cursor-not-allowed text-muted-foreground opacity-60"
                  : "hover:bg-accent"
              }`}
              title="Disponivel apenas para Gemini conectado"
            >
              Conta conectada
            </button>
          )}
        </div>

        <div class="mt-4">
          <label class="mb-2 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Provedor
          </label>
          <div class="flex flex-wrap gap-2">
            {[
              ["gemini", "Gemini"],
              ["openai", "GPT / Codex"],
              ["anthropic", "Claude"],
              ["xai", "Grok"],
              ["deepseek", "DeepSeek"]
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() =>
                  setAiProvider(
                    value as "gemini" | "openai" | "anthropic" | "xai" | "deepseek"
                  )
                }
                aria-pressed={prefs.aiProvider === value ? "true" : "false"}
                class={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                  prefs.aiProvider === value
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background hover:bg-accent"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div class="mt-4 space-y-2">
          <label class="block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Modelo opcional
          </label>
          <input
            type="text"
            value={prefs.aiModel}
            placeholder={
              prefs.aiProvider === "openai"
                ? "Ex.: gpt-5-mini ou gpt-5.2-codex"
                : "Deixe vazio para usar o padrão do provedor"
            }
            onInput={(e) => setAiModel((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30"
          />
          <div class="flex flex-wrap gap-2">
            {providerCatalog.models.map((model) => (
              <button
                key={model.id}
                type="button"
                onClick={() => setAiModelPreset(model.id, model.value)}
                class={`rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors ${
                  prefs.aiModel === model.value &&
                  prefs.aiModelCatalogId === model.id
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-background hover:bg-accent"
                }`}
                title={model.description}
              >
                <span class="font-medium">{model.label}</span>
                <span class="ml-1 text-[10px] text-muted-foreground">
                  {model.recommended
                    ? "recomendado"
                    : model.freeFriendly
                      ? "bom no grátis"
                      : model.kind === "coding"
                        ? "técnico"
                        : model.kind === "reasoning"
                          ? "raciocínio"
                          : "geral"}
                </span>
              </button>
            ))}
          </div>
        </div>

        {prefs.aiAccessMode === "byok" && (
          <div class="mt-4 space-y-2">
            <label class="block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              API Key
            </label>
            <input
              type="password"
              value={prefs.aiByokApiKey}
              placeholder="Cole sua chave do provedor selecionado"
              onInput={(e) =>
                setAiByok(
                  prefs.aiProvider,
                  (e.target as HTMLInputElement).value,
                  data.value?.user?.id ?? ""
                )
              }
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30"
            />
            <p class="text-xs text-muted-foreground">
              A chave fica salva localmente no seu navegador
              (<code class="font-mono text-[11px]">localStorage</code>). É
              enviada apenas pro servidor quando você faz uma pergunta — o
              servidor usa-a pra chamar o provedor em seu nome e descarta. Não
              persistimos a chave em banco nem em log.
              {" "}
              <a
                href={providerCatalog.keyUrl}
                target="_blank"
                rel="noopener noreferrer"
                class="text-primary hover:underline"
              >
                Abrir {providerCatalog.keyLabel}
              </a>
              .
            </p>
          </div>
        )}

        <div class="mt-4 rounded-md border border-dashed border-border bg-background/70 px-3 py-2 text-xs text-muted-foreground">
          <strong class="text-foreground">Status da integracao:</strong>{" "}
          {providerCatalog.connectionNote ??
            strategy?.note ??
            "Hoje o suporte real aqui e por chave da plataforma ou por sua propria API key."}
        </div>
      </section>

      <section class="rounded-md border border-border bg-card/60 p-5">
        <h2 class="text-base font-bold">Conta conectada</h2>
        <p class="mt-1 text-sm text-muted-foreground">
          Hoje a conexao de conta esta disponivel apenas para Gemini via
          Google. Os demais provedores seguem no fluxo `Minha API key`.
        </p>
        <div class="mt-3 rounded-md border border-border bg-background/70 px-3 py-2 text-xs text-muted-foreground">
          Para o aluno, o fluxo e simples: clicar em <strong class="text-foreground">Conectar Google</strong>,
          entrar na conta e autorizar o acesso. A configuracao de OAuth do Google
          e uma etapa unica da plataforma, nao do usuario final.
        </div>
        {googleConnectNotice && (
          <div class="mt-3 rounded-md border border-border bg-background/70 px-3 py-2 text-xs text-muted-foreground">
            {googleConnectNotice}
          </div>
        )}
        <div class="mt-4 grid gap-3 md:grid-cols-2">
          {(connections?.connections ?? []).map((item) => (
            <div
              key={item.provider}
              class="rounded-lg border border-border bg-background/70 px-4 py-3"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="font-medium">{item.label}</div>
                <span class="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {item.availableNow ? "disponivel" : "em breve"}
                </span>
              </div>
              <p class="mt-2 text-xs text-muted-foreground">{item.message}</p>
              <div class="mt-3 flex flex-wrap gap-2">
                {item.canStartOAuth && item.status !== "connected" ? (
                  <button
                    type="button"
                    onClick={() => {
                      const returnTo = `${window.location.pathname}${window.location.search}`;
                      window.location.href = `/api/student/ai-google-connect?returnTo=${encodeURIComponent(returnTo)}`;
                    }}
                    class="rounded-md border border-primary bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:opacity-90"
                  >
                    Conectar Google
                  </button>
                ) : null}
                {item.status === "connected" ? (
                  <button
                    type="button"
                    onClick={() => {
                      void fetch("/api/student/ai-google-connect", {
                        method: "DELETE",
                        credentials: "include"
                      })
                        .then(() => api.aiConnections())
                        .then((resp) => {
                          setConnections(resp);
                          setAiAccessMode("platform");
                        })
                        .catch(() => {});
                    }}
                    class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent"
                  >
                    Desconectar
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    void api
                      .updateAiConnectionInterest(item.provider, !item.interested)
                      .then((resp) => {
                        setConnections((current) =>
                          current
                            ? {
                                ...current,
                                connections: current.connections.map((conn) => ({
                                  ...conn,
                                  interested: resp.interestedProviders.includes(conn.provider)
                                }))
                              }
                            : current
                        );
                      })
                      .catch(() => {});
                  }}
                  class={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                    item.interested
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border bg-background hover:bg-accent"
                  }`}
                >
                  {item.interested ? "Interesse marcado" : "Marcar interesse"}
                </button>
              </div>
            </div>
          ))}
          {!connections && (
            <div class="rounded-lg border border-dashed border-border bg-background/50 px-4 py-3 text-xs text-muted-foreground md:col-span-2">
              A estrutura de conta conectada ja esta reservada no produto, mas
              ainda sem fluxo de autorizacao ativo.
            </div>
          )}
        </div>
      </section>

      <section class="rounded-md border border-border bg-card/60 p-5">
        <h2 class="text-base font-bold">Como o tutor funciona</h2>
        <ul class="mt-2 space-y-2 text-sm text-muted-foreground">
          <li>
            <strong class="text-foreground">Persona por tema:</strong> o
            backend detecta o tema da questão (cardiologia, direito
            tributário, matemática…) e a IA assume a "persona" de um
            professor dessa área.
          </li>
          <li>
            <strong class="text-foreground">Profundidade adaptativa:</strong>{" "}
            por padrão trata você como leigo no tema; quando você demonstra
            domínio, ela sobe o nível.
          </li>
          <li>
            <strong class="text-foreground">Contexto travado:</strong> a IA
            só responde sobre a questão atual. Perguntas fora desse escopo
            são redirecionadas.
          </li>
          <li>
            <strong class="text-foreground">Sem spoiler ao trocar:</strong>{" "}
            quando você muda de questão, a conversa daquela questão é
            preservada, mas a nova começa do zero.
          </li>
        </ul>
      </section>
    </div>
  );
}

// =============================================================================
// Pages
// =============================================================================

function LoadingPage() {
  // Loader "orbit" portado do legado (Simulador.html .app-loading-overlay).
  // Card centralizado com anel rotativo violet/indigo + selo pulsante e
  // o logo do simulador no centro. Mais presente visualmente que skeletons
  // mortos enquanto o bootstrap inicial roda.
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Carregando painel do aluno"
      class="app-loading-overlay fixed inset-0 z-[10050] flex items-center justify-center p-5"
    >
      <div class="flex w-full max-w-[360px] min-w-[280px] flex-col items-center gap-[18px] rounded-xl border border-border bg-card px-8 py-7 shadow-[0_24px_48px_-12px_rgba(15,23,42,0.28)]">
        <div class="app-loading-orbit" aria-hidden="true">
          <div class="app-loading-orbit-ring"></div>
        </div>
        <div class="flex flex-col items-center gap-1 text-center">
          <div class="text-base font-bold text-foreground">Carregando…</div>
          <div class="max-w-[260px] text-xs leading-relaxed text-muted-foreground">
            Trazendo seus bancos, provas e progresso.
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorPage() {
  return (
    <main class="container mx-auto px-4 py-12">
      <Alert variant="destructive" className="max-w-2xl">
        <AlertCircle width="1.3em" height="1.3em" />
        <AlertTitle>Não foi possível carregar</AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p>{errorMsg.value}</p>
          <div class="flex gap-2">
            <Button size="sm" onClick={bootstrap}>
              Tentar de novo
            </Button>
            <Button size="sm" variant="outline" onClick={goSwitch}>
              Voltar para login
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </main>
  );
}

function Breadcrumbs() {
  const ws = studyWorkspace.value;
  const provaWs = provaWorkspace.value;
  const resultWs = provaResultView.value;
  const revisaoWs = provaRevisaoView.value;
  const schedWs = scheduledExamView.value;
  const aiStudy = activeAiStudySession.value;
  const dissWs = dissertativeWorkspace.value;
  // Dissertativa IA-gerada: Bancos / <Nome do banco> / Dissertativa
  // Karol 2026-05-29: clicar "Meu acervo de bancos" no header não fechava
  // o workspace dissertativo — caía no fallback que não chamava exit().
  if (dissWs) {
    const bankName = dissertativeWorkspaceBankName.value ?? "Carregando…";
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.bancos,
        onClick: () => {
          exitDissertativeWorkspace();
          closeBankDetail();
          setActiveNav("bancos");
        }
      },
      {
        label: bankName,
        onClick: () => {
          exitDissertativeWorkspace();
          setActiveNav("bancos");
          openBank(dissWs.bankId);
        }
      },
      {
        label:
          dissWs.mode === "estudo"
            ? "Estudo dissertativo"
            : "Simulação dissertativa"
      }
    ];
    return <BreadcrumbList items={items} />;
  }
  // Estudando com IA: Estudando com IA / <Nome do caderno ou redação>
  if (aiStudy) {
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.aistudy,
        onClick: () => {
          // Fecha o workspace e volta pra lista da seção.
          activeAiStudySessionId.value = null;
          activeAiStudySession.value = null;
          setActiveNav("aistudy");
        }
      },
      { label: aiStudy.title || "Sem título" }
    ];
    return <BreadcrumbList items={items} />;
  }
  if (schedWs) {
    const title = schedWs.exam?.title ?? "Carregando…";
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.provas,
        onClick: () => {
          exitScheduledExamView();
          setActiveNav("provas");
        }
      },
      { label: title }
    ];
    return <BreadcrumbList items={items} />;
  }
  // Quando ha workspace de estudo ativo, breadcrumb vira:
  //   Bancos / <Nome do banco> / Modo Estudo
  // "Bancos" sempre volta pra LISTA (closeBankDetail). O nome do banco volta
  // pra TELA DE DETALHE daquele banco (openBank), pra o aluno escolher
  // outra ação sem ter que selecionar o banco de novo.
  if (ws) {
    const bankName = ws.data?.bank.name ?? "Carregando…";
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.bancos,
        onClick: () => {
          exitStudyWorkspace();
          closeBankDetail();
          setActiveNav("bancos");
        }
      },
      {
        label: bankName,
        onClick: () => {
          exitStudyWorkspace();
          setActiveNav("bancos");
          openBank(ws.bankId);
        }
      },
      { label: "Modo Estudo" }
    ];
    return <BreadcrumbList items={items} />;
  }
  // Prova em andamento: Bancos / <Nome do banco> / Prova
  if (provaWs) {
    const bankName = provaWs.data?.bank.name ?? "Carregando…";
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.bancos,
        onClick: () => {
          exitProvaWorkspace();
          closeBankDetail();
          setActiveNav("bancos");
        }
      },
      {
        label: bankName,
        onClick: () => {
          exitProvaWorkspace();
          setActiveNav("bancos");
          openBank(provaWs.bankId);
        }
      },
      { label: "Prova em andamento" }
    ];
    return <BreadcrumbList items={items} />;
  }
  // Resultado de prova encerrada: Bancos / <Nome do banco> / Resultado
  if (resultWs) {
    const bankName = resultWs.bank?.name ?? "Carregando…";
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.bancos,
        onClick: () => {
          exitProvaResultView();
          closeBankDetail();
          setActiveNav("bancos");
        }
      },
      {
        label: bankName,
        onClick: () => {
          exitProvaResultView();
          setActiveNav("bancos");
          openBank(resultWs.bankId);
        }
      },
      { label: "Resultado da prova" }
    ];
    return <BreadcrumbList items={items} />;
  }
  // Revisão da prova: Bancos / <Nome do banco> / Resultado / Revisão
  if (revisaoWs) {
    const bankName = revisaoWs.bank?.name ?? "Carregando…";
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.bancos,
        onClick: () => {
          exitProvaRevisaoView();
          closeBankDetail();
          setActiveNav("bancos");
        }
      },
      {
        label: bankName,
        onClick: () => {
          exitProvaRevisaoView();
          setActiveNav("bancos");
          openBank(revisaoWs.bankId);
        }
      },
      {
        label: "Resultado",
        onClick: () => {
          // Volta pro resultado da mesma prova.
          const url = `/student/banks/${encodeURIComponent(
            revisaoWs.bankId
          )}/prova/${encodeURIComponent(revisaoWs.provaId)}/resultado`;
          window.history.pushState({}, "", url);
          exitProvaRevisaoView();
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      },
      { label: "Revisão" }
    ];
    return <BreadcrumbList items={items} />;
  }
  // Detalhe de banco aberto (sem workspace ativo): Bancos / <Nome do banco>
  if (activeNav.value === "bancos" && activeBankId.value) {
    const items: SubCrumb[] = [
      {
        label: SECTION_LABEL.bancos,
        onClick: closeBankDetail
      },
      { label: activeBankName.value ?? "Carregando…" }
    ];
    return <BreadcrumbList items={items} />;
  }
  const section: SubCrumb = {
    label: SECTION_LABEL[activeNav.value],
    onClick:
      subCrumbs.value.length > 0
        ? () => (subCrumbs.value = [])
        : undefined
  };
  const items = [section, ...subCrumbs.value];
  return <BreadcrumbList items={items} />;
}

function BreadcrumbList({ items }: { items: SubCrumb[] }) {
  return (
    <nav class="flex min-w-0 items-center gap-1.5 text-sm" aria-label="Trilha">
      {items.map((c, i) => {
        const isLast = i === items.length - 1;
        return (
          <>
            {i > 0 && (
              <ChevronRight width="1.3em" height="1.3em" className="shrink-0 text-primary" />
            )}
            {c.onClick && !isLast ? (
              <button
                type="button"
                onClick={c.onClick}
                class="truncate rounded text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
              >
                {c.label}
              </button>
            ) : (
              <span
                class={`truncate ${
                  isLast ? "font-semibold text-foreground" : "text-muted-foreground"
                }`}
              >
                {c.label}
              </span>
            )}
          </>
        );
      })}
    </nav>
  );
}

function ReadyPage({ home }: { home: StudentHomeResponse }) {
  const collapsed = sidebarCollapsed.value;
  // Margem esquerda do main pra compensar sidebar fixa no desktop
  const mainOffset = collapsed ? "md:ml-16" : "md:ml-72";
  return (
    <div class="min-h-screen">
      <Sidebar home={home} />
      <div class={`flex min-h-screen min-w-0 flex-col transition-[margin] duration-200 ${mainOffset}`}>
        {/* Top bar com hamburguer (mobile) + breadcrumbs (sempre) */}
        <div class="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/80 px-5 py-3 backdrop-blur-md md:px-8">
          <MobileMenuButton />
          <Breadcrumbs />
        </div>

        <main class="flex-1 px-5 py-6 md:px-8 md:py-8">
          {/* Ordem de precedência: revisão > resultado > prova em andamento > estudo > views normais.
              Cada workspace tem sua própria URL/state; só um renderiza por vez. */}
          {scheduledExamView.value ? (
            <ScheduledExamWorkspaceView />
          ) : provaRevisaoView.value ? (
            <ProvaRevisaoViewComp />
          ) : provaResultView.value ? (
            <ProvaResultViewComp />
          ) : provaWorkspace.value ? (
            <ProvaWorkspaceView />
          ) : studyWorkspace.value ? (
            <StudyWorkspaceView />
          ) : dissertativeWorkspace.value ? (
            <DissertativeWorkspaceView />
          ) : (
            <>
              {activeNav.value === "home" && <HomeView home={home} />}
              {activeNav.value === "bancos" && (
                activeBankId.value ? (
                  <BankDetailView />
                ) : (
                  <>
                    <header class="mb-6">
                      <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
                        <Database className="size-6 text-primary" />
                        Meu acervo de bancos
                      </h1>
                      <p class="mt-1 text-sm text-muted-foreground">
                        Treine com um dos bancos disponíveis para você.
                      </p>
                    </header>
                    <BanksView />
                  </>
                )
              )}
              {activeNav.value === "disciplinas" && <MateriasView />}
              {(activeNav.value === "mensagens" ||
                activeNav.value === "notificacoes") && <InboxCenterView />}
              {activeNav.value === "agenda" && <AgendaView />}
              {activeNav.value === "minhaconta" && <MinhaContaView />}
              {activeNav.value === "provas" && <MinhasProvasView />}
              {activeNav.value === "jogos" && <JogosView />}
              {activeNav.value === "ia" && (
                <PagamentosView
                  initialTab="ia"
                  aiSettingsSlot={<AiSettingsView embedded />}
                />
              )}
              {activeNav.value === "gravador" && <RecorderView />}
              {activeNav.value === "compartilhamentos" && <SharesView />}
              {(activeNav.value === "aistudy" ||
                activeNav.value === "quickstudy") && <AiStudyView />}
              {activeNav.value === "assinatura" && (
                <PagamentosView initialTab="gastos" aiSettingsSlot={<AiSettingsView embedded />} />
              )}
              {activeNav.value === "controlegastos" && (
                <PagamentosView initialTab="gastos" aiSettingsSlot={<AiSettingsView embedded />} />
              )}
              {activeNav.value === "formapagamento" && (
                <PagamentosView initialTab="cartoes" aiSettingsSlot={<AiSettingsView embedded />} />
              )}
              {activeNav.value === "meusreports" && <MeusReportsView />}
              {activeNav.value === "conexoes" && <ConexoesView />}
              {activeNav.value === "indicar" && <ReferralView />}
              {activeNav.value === "descobrir" && <DiscoverBanksView />}
              {activeNav.value === "anotacoes" && <AnotacoesHubView />}
              {activeNav.value === "gruposestudo" && <StudyGroupsView />}
              {activeNav.value === "peerchat" && <PeerChatHubView />}
              {activeNav.value === "turmas" && isPlatformAdmin.value && <TurmasView />}
              {activeNav.value === "faq" && <FaqView />}
            </>
          )}
        </main>
        <footer class="hidden border-t border-border bg-card/30 px-5 py-3 text-center text-[11px] text-muted-foreground/70 md:block md:px-8">
          dev · /student/ · Ctrl+/ ou Ctrl+\ alternam o tema · Ctrl+B alterna a sidebar
        </footer>
      </div>
    </div>
  );
}

// =============================================================================
// Root
// =============================================================================

/** Banner persistente no topo da tela quando admin está em Impersonate.
 *  Avisa o "observador" que está em modo READ-ONLY + botão pra sair.
 *  [[project-admin-impersonate-readonly]] */
function ImpersonateBanner() {
  const info = impersonatingInfo.value;
  if (!info) return null;
  async function stop() {
    try {
      await api.impersonateStop();
    } catch {
      /* segue mesmo se falhar */
    }
    // Hard reload pra voltar o session ao actor (admin).
    window.location.href = "/manager/";
  }
  return (
    <div class="fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-3 border-b-2 border-amber-500 bg-amber-500/90 px-4 py-2 text-xs font-semibold text-amber-950 shadow-md backdrop-blur-sm dark:bg-amber-600/90 dark:text-amber-50">
      <span class="inline-flex items-center gap-2">
        <Eye className="size-4" />
        Modo Impersonate · você está vendo como {data.value?.user.name ?? "este usuário"}
        {info.actorName ? ` (admin: ${info.actorName})` : ""} · ações de mudança estão bloqueadas
      </span>
      <button
        type="button"
        onClick={stop}
        class="rounded-md border border-amber-950/30 bg-white/80 px-2 py-0.5 text-[11px] hover:bg-white dark:bg-amber-50/90"
      >
        Sair do modo
      </button>
    </div>
  );
}

export { CadernoPopupPage } from "./CadernoPopupPage";

export function App() {
  // Pop-up dedicado do Gravador de Aulas: quando aberto via window.open com
  // ?view=recorder-window, monta SÓ o RecorderWindow (sem header, menu,
  // bootstrap etc.). Isso evita refresh acidental do app principal durante
  // a gravação. O check é síncrono — feito antes de qualquer useEffect.
  const isRecorderPopup = (() => {
    try {
      return new URL(window.location.href).searchParams.get("view") === "recorder-window";
    } catch {
      return false;
    }
  })();

  if (isRecorderPopup) {
    return <RecorderWindow />;
  }

  // Pop-up dedicado das Anotações do banco: aberto pelo AiAssistant
  // (tab Anotações no Modo Estudo). Monta só o painel de anotações sem
  // header/menu. Sincroniza com a janela principal via BroadcastChannel
  // pra desabilitar a tab inline enquanto o pop-up estiver aberto.
  const notesPopup = (() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("view") !== "notes-window") return null;
      const bankId = url.searchParams.get("bankId") ?? "";
      if (!bankId) return null;
      return { bankId };
    } catch {
      return null;
    }
  })();

  if (notesPopup) {
    return <NotesWindow bankId={notesPopup.bankId} />;
  }

  // Pop-up dedicado do Tira-dúvida (QuickStudy) — abrir via window.open
  // de qualquer surface (card, dock, view). [[project-quickstudy]]
  const isQuickStudyPopup = (() => {
    try {
      return new URL(window.location.href).searchParams.get("view") === "quickstudy-window";
    } catch {
      return false;
    }
  })();
  if (isQuickStudyPopup) {
    return <QuickStudyWindow />;
  }

  // Pop-up dedicado de UMA anotação — aberto pelo hub "Minhas anotações"
  // (AnotacoesHubView). Mostra a nota em fullscreen, auto-save. O hub usa
  // useSingleNoteWindowOpen pra desabilitar o card enquanto o pop-up está vivo.
  // [[project-notes-hub-popout]]
  const noteWindow = (() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("view") !== "note-window") return null;
      const noteId = url.searchParams.get("noteId") ?? "";
      if (!noteId) return null;
      return { noteId };
    } catch {
      return null;
    }
  })();
  if (noteWindow) {
    return <SingleNoteWindow noteId={noteWindow.noteId} />;
  }

  // Pop-up dedicado do Tutor IA: aberto pelo botão pop-out no painel direito
  // do Modo Estudo. Contexto da questão é sincronizado via BroadcastChannel.
  const aiWindow = (() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("view") !== "ai-window") return null;
      const bankId = url.searchParams.get("bankId") ?? "";
      if (!bankId) return null;
      return { bankId };
    } catch {
      return null;
    }
  })();
  if (aiWindow) {
    return <AiAssistantWindow bankId={aiWindow.bankId} />;
  }

  useEffect(() => {
    bootstrap();
  }, []);

  useEffect(() => {
    const onJogosBreadcrumb = (event: Event) => {
      const detail = (event as CustomEvent<JogosBreadcrumbDetail>).detail;
      const label = detail?.label?.trim();
      if (activeNav.value !== "jogos") return;
      subCrumbs.value = label ? [{ label }] : [];
    };

    window.addEventListener(
      "student:jogos-breadcrumb",
      onJogosBreadcrumb as EventListener
    );

    return () => {
      window.removeEventListener(
        "student:jogos-breadcrumb",
        onJogosBreadcrumb as EventListener
      );
    };
  }, []);

  // Auto-abre o ProvaStartModal quando o ProvaWorkspace entra em ready sem
  // prova ativa (caso: aluno clicou em "Iniciar nova simulação" no chooser
  // e veio pra cá, mas o banco não tem ativa pra retomar). O modal fica
  // por cima do <ProvaSemAtiva /> que fica no fundo.
  const provaWs = provaWorkspace.value;
  useEffect(() => {
    if (
      provaWs?.phase === "ready" &&
      !provaWs.prova &&
      provaWs.data &&
      provaWs.data.questoes.length > 0
    ) {
      openProvaStartModal(provaWs.bankId, provaWs.data.questoes);
    }
  }, [provaWs?.bankId, provaWs?.phase, provaWs?.prova, provaWs?.data]);

  return (
    <>
      <MeshBackground />
      <ImpersonateBanner />
      <div class="min-h-screen text-foreground">
        {phase.value === "loading" && <LoadingPage />}
        {phase.value === "error" && <ErrorPage />}
        {phase.value === "ready" && data.value && <ReadyPage home={data.value} />}
      </div>
      <GuiaModal />
      <PreferencesModal />
      <PdfExportModal />
      <CompraPendenteModal />
      <ProvaStartModal />
      <ConfirmRoot />
      <PromptRoot />
      <ShareDialog />
      <UploadBankModal />
      <PeerChatDock />
      <InboxDock />
      <QuickStudyDock />
      <GenerateBankDock />
      <WelcomeModal />
    </>
  );
}
